/**
 * As peças pequenas que se repetem na mesa inteira — agora sobre o shadcn.
 *
 * A tela tem TRÊS camadas, e a divisão importa:
 *
 * 1. `ui/` é o registro do shadcn (preset `base-luma`, primitivas do
 *    `@base-ui/react`). Os arquivos de lá vêm em inglês e são REGERÁVEIS
 *    (`shadcn add --overwrite`): o que se mexe ali é tema, não comportamento.
 * 2. Este arquivo é a CASA: nome em português, medida do desenho, o vocabulário
 *    que as telas conhecem (`Cartao`, `Seletor`, `Etiqueta`, `Cortina`). Cada
 *    peça aqui é um componente do shadcn vestido com a paleta do escritório.
 * 3. As vistas e sobreposições, que só falam com esta camada.
 *
 * Por que a camada do meio existe: as telas chamam `<Seletor opcoes=…>` em
 * dezenas de lugares. Se cada uma montasse `Select`+`SelectTrigger`+
 * `SelectContent` à mão, trocar o raio do seletor viraria trinta edições — e
 * seria só uma questão de tempo até dois seletores discordarem. A API daqui é
 * a mesma de antes do shadcn, de propósito: a troca de motor não pediu uma
 * linha de mudança em quem usa.
 *
 * O que era acessibilidade escrita à mão (foco preso, Esc, seta, `role`) agora
 * é do `@base-ui/react`. O que era paleta escrita à mão em cada bloco agora é
 * token (`bg-card`, `text-muted-foreground`, `border-border`) — os mesmos
 * hexadecimais de sempre, num lugar só (`index.css`).
 */
import { useEffect, useId, useRef, useState } from "react";
import { useNavegacao } from "../navegacao.jsx";
import { useEstado } from "../estado.jsx";
import { Avatar as AvatarRaiz, AvatarFallback, AvatarImage } from "@/ui/avatar";
import { Badge } from "@/ui/badge";
import { Button } from "@/ui/button";
import { Card } from "@/ui/card";
import { Checkbox } from "@/ui/checkbox";
import { Dialog, DialogContent, DialogOverlay, DialogPortal, DialogTitle } from "@/ui/dialog";
import { Dialog as DialogBase } from "@base-ui/react/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "@/ui/dropdown-menu";
import { Empty, EmptyContent, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from "@/ui/empty";
import { Field, FieldDescription, FieldLabel } from "@/ui/field";
import { InputGroup, InputGroupAddon, InputGroupButton, InputGroupInput } from "@/ui/input-group";
import { Input } from "@/ui/input";
import { Kbd } from "@/ui/kbd";
import { Label } from "@/ui/label";
import { RadioGroup, RadioGroupItem } from "@/ui/radio-group";
import { ScrollArea } from "@/ui/scroll-area";
import { Progress } from "@/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/ui/select";
import { Alert, AlertDescription } from "@/ui/alert";
import { Spinner } from "@/ui/spinner";
import { Switch } from "@/ui/switch";
import { ToggleGroup, ToggleGroupItem } from "@/ui/toggle-group";
import { cn } from "@/lib/utils";
import { corAvatar, Icone, ICONES, iniciais, s, tinta } from "../visual.jsx";
import { CampoDeData } from "./CampoDeData.tsx";
import { ausencia } from "../ausencia.js";
import { useGravou } from "../gravou.js";
import { CAMPO, FOCO_DENTRO } from "./pele.ts";
import { confirmar } from "@/componentes/pergunta.tsx";
import * as api from "../api.js";
/* A tabela de DDD é a do servidor: a bandeira da lista e o mapa da carteira
   respondem "de onde é" com a mesma conta. */
import { ESTADOS, ufDoTelefone } from "../../../src/uf.ts";

/**
 * O quadradinho da pessoa: a foto quando ela tem (`foto`), iniciais quando não.
 * Cor estável por nome.
 *
 * A foto entra AQUI porque a mesma pessoa aparece na trilha, na transferência e
 * no trilho — cada tela com o seu próprio `<img>` era a foto sumindo em três
 * lugares de cada vez.
 *
 * O `Avatar` do shadcn é quem cuida do que era o estado `quebrou`: a imagem que
 * não carrega cede lugar ao `AvatarFallback` sozinha, sem `onError` à mão.
 *
 * QUATRO degraus (20, 28, 36, 48), e o tamanho pedido cai no mais perto. Eram
 * treze medidas, cada uma com a letra escolhida à mão, e as pequenas desciam a
 * 8px — duas iniciais que ninguém lê. No degrau de 20 a letra não cabe legível
 * (11px num círculo de 20 encosta na borda), então ele é só a COR, e o nome vai
 * para o `aria-label` e para a dica. Do 28 para cima a letra tem piso de 11.
 *
 * `apagado` é o arquivado: quem apaga é o avatar, nunca o texto ao lado — o nome
 * de um caso arquivado continua sendo lido.
 */
const DEGRAUS_DO_AVATAR = [
  { lado: 20, fonte: 0 },
  { lado: 28, fonte: 11 },
  { lado: 36, fonte: 12.5 },
  { lado: 48, fonte: 16 },
];

export const degrauDoAvatar = (tamanho) =>
  DEGRAUS_DO_AVATAR.reduce((perto, d) => (Math.abs(d.lado - tamanho) < Math.abs(perto.lado - tamanho) ? d : perto));

/* `fonte` continua aceita e é ignorada: a letra é do degrau, e treze telas
   escolhendo a sua era o que fazia a mesma inicial ter cinco tamanhos. */
/**
 * O "rosto" da IA: o ícone da casa num círculo verde — o mesmo que marca o caso
 * conduzido na lista. Mora aqui porque a fila da IA aparece em três lugares (o
 * filtro da mesa, o painel e a linha do caso) e precisa ser o MESMO símbolo nos
 * três, ou a pessoa não liga a bolinha ao robozinho da linha.
 */
export function RostoDaIa({ tamanho = 28 }) {
  return (
    <span
      aria-hidden="true"
      className="flex flex-none items-center justify-center rounded-full bg-verde text-sobre-terra"
      style={s(`width:${tamanho}px;height:${tamanho}px`)}
    >
      <Icone de={ICONES.agenteIa} tamanho={Math.round(tamanho * 0.64)} />
    </span>
  );
}

export function Avatar({ nome, foto, tamanho = 36, raio = 999, cor, apagado = false }) {
  const { lado, fonte } = degrauDoAvatar(tamanho);
  const mudo = fonte === 0;
  return (
    <AvatarRaiz
      className={cn("flex-none", apagado && "opacity-55 grayscale")}
      style={{ width: lado, height: lado, borderRadius: raio, fontSize: fonte || undefined }}
      {...(mudo && nome ? { role: "img", "aria-label": nome, "data-dica": nome } : {})}
    >
      {foto ? <AvatarImage src={foto} alt="" loading="lazy" className="object-cover" /> : null}
      <AvatarFallback
        className="font-bold text-sobre-escuro"
        style={{ background: cor ?? corAvatar(nome), borderRadius: raio, fontSize: fonte || undefined }}
      >
        {mudo ? null : iniciais(nome)}
      </AvatarFallback>
    </AvatarRaiz>
  );
}

/* O nome como chave: "João Victor", "JOÃO VICTOR" e "joao victor" são a mesma
   pessoa da equipe — a ficha guarda em caixa alta, o login em caixa mista. */
const chaveDoNome = (n) =>
  String(n ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toUpperCase();

/**
 * O ROSTO — o único avatar da mesa, e ele sempre procura a foto (29/10).
 *
 * Eram três caminhos: `Avatar` com `foto` passada à mão (metade das telas
 * passava `p.foto`, a outra metade `p.avatar`, e dez não passavam nada), e o
 * `Retrato` que só pedia a foto do cliente quando o servidor já dizia que ela
 * existia — então o caso novo ficava em iniciais para sempre. Agora quem
 * desenha diz QUEM é, e o rosto acha a foto:
 *
 *   - `pessoa` — alguém da EQUIPE, pelo nome (string) ou pela linha
 *     (`{ nome, avatar, cor }`): a foto sai do cadastro da equipe;
 *   - `caso` — o id do caso: a foto do WhatsApp do cliente, que o servidor
 *     busca na hora se ainda não tem (`whatsapp.avatarDoCaso`);
 *   - `jid` — um contato do aparelho;
 *   - `foto` — a URL pronta, quando quem chama já a tem (o próprio usuário).
 *
 * Sem foto, as iniciais na cor estável do nome. `Avatar` é a peça de dentro, e
 * `visual.test.jsx` reprova tela que a use direto.
 */
export function Rosto({ pessoa, caso, registro, jid, nome, foto, tamanho = 36, raio = 999, cor, apagado }) {
  const { equipe, fotos } = useEstado();
  const linha = pessoa && typeof pessoa === "object" ? pessoa : null;
  const nomeDaPessoa = linha?.nome ?? (typeof pessoa === "string" ? pessoa : null);
  const daEquipe = nomeDaPessoa ? (equipe ?? []).find((p) => chaveDoNome(p.nome) === chaveDoNome(nomeDaPessoa)) : null;
  const idDoCaso = caso ?? registro;
  const url =
    foto ??
    linha?.avatar ??
    daEquipe?.avatar ??
    (nomeDaPessoa ? fotos?.[chaveDoNome(nomeDaPessoa)] : null) ??
    (idDoCaso ? api.avatarDoRegistro(idDoCaso) : null) ??
    (jid ? api.avatarDoJid(jid) : null) ??
    null;
  return (
    <Avatar
      nome={nomeDaPessoa ?? nome}
      foto={url}
      tamanho={tamanho}
      raio={raio}
      cor={cor ?? linha?.cor ?? daEquipe?.cor}
      apagado={apagado}
    />
  );
}

/** O nome antigo do `Rosto` — as telas que o chamavam continuam valendo. */
export const Retrato = Rosto;

/**
 * A bandeira do estado do contato, lida do DDD (`src/uf.ts`). Sem DDD que diga
 * — número de fora, sem telefone — não desenha nada: bandeira errada é pior
 * que nenhuma. Recebe `uf` pronta (o mapa já agrupou) ou o `telefone`.
 */
export function Bandeira({ telefone, uf = ufDoTelefone(telefone), altura = 12, classe = "" }) {
  if (!uf) return null;
  return (
    <img
      src={`/bandeiras/${uf}.png`}
      alt={ESTADOS[uf]}
      data-dica={ESTADOS[uf]}
      loading="lazy"
      className={cn("flex-none rounded-[2px] object-cover shadow-[0_0_0_1px_var(--color-border)]", classe)}
      style={{ height: altura, width: Math.round(altura * 1.43) }}
    />
  );
}

/**
 * Etiqueta de tese, etapa ou estado. Sem `cores`, tira a cor do próprio texto.
 *
 * 24 de altura, 12/600, raio 6 — a medida que cabe numa linha de lista sem
 * crescer a linha, e a letra que se lê: era 11px num selo de 19, o menor texto
 * da mesa carregando justamente a TESE do caso.
 *
 * `ponto` é o círculo de 6px na cor da letra (`currentColor`): o estado se diz
 * também pela forma, e não só pelo tom, para quem não separa as cores.
 */
export function Etiqueta({ children, cores, peso = 600, discreta = false, ponto = false, classe }) {
  const [fundo, letra] =
    cores ??
    (discreta ? ["color-mix(in oklab, var(--color-foreground) 8%, transparent)", "var(--color-tinta-suave)"] : tinta(children));
  return (
    <Badge
      className={cn(
        "h-6 gap-1.5 whitespace-nowrap rounded-etiqueta px-[9px] py-0 text-[12px] leading-none tracking-[.005em]",
        classe,
      )}
      style={{ background: fundo, color: letra, fontWeight: peso }}
    >
      {ponto ? <span aria-hidden="true" className="size-1.5 flex-none rounded-full bg-current" /> : null}
      {children}
    </Badge>
  );
}

/** "99+" em todo lugar: número de três dígitos alarga o selo e empurra a linha. */
export const contagem = (n) => (n > 99 ? "99+" : String(n));

/**
 * O CONTADOR — o número redondo de não lidas, de pendências, de itens.
 *
 * Era desenhado à mão em cada tela, com quatro alturas e duas regras para o
 * número grande. Aqui: 20 de altura, pílula, 11/700 tabular (os dígitos têm a
 * mesma largura, e o selo não dança quando 9 vira 10), "99+" acima de 99.
 *
 * `tom` diz o papel, não o gosto: `terra` pede ação (não lidas), `quieto` é só
 * contagem, `tinta` é o neutro forte, `ambar` é espera. `sobre` põe o contorno
 * do fundo em volta, para o contador pendurado no canto de um ícone ou avatar.
 *
 * Quando o número SOBE, o selo dá um pulo — é a única coisa na tela dizendo "chegou
 * mais uma" sem som. Descer não pula: ler uma mensagem não é notícia. Zero não
 * desenha nada, a menos que `zero` peça (contagem em que o zero é informação).
 */
const TONS_DO_CONTADOR = {
  terra: "bg-terra text-sobre-terra",
  quieto: "bg-foreground/8 text-tinta-fraca",
  tinta: "bg-tinta text-mesa",
  ambar: "bg-ambar text-sobre-ambar",
};

export function Contador({ n, tom = "terra", sobre = false, zero = false, rotulo, classe = "" }) {
  const anterior = useRef(n);
  const [pulo, setPulo] = useState(0);
  useEffect(() => {
    if (n > anterior.current) setPulo((v) => v + 1);
    anterior.current = n;
  }, [n]);
  if (!n && !zero) return null;
  return (
    <span
      key={pulo}
      aria-label={rotulo}
      className={cn(
        "inline-grid h-5 min-w-5 flex-none place-items-center rounded-full px-1.5 text-selo leading-none font-bold tabular-nums",
        TONS_DO_CONTADOR[tom],
        sobre && "shadow-[0_0_0_2px_var(--color-card)]",
        pulo > 0 && "animate-[vPop_var(--t-media)_var(--e-mola)]",
        classe,
      )}
    >
      {contagem(n ?? 0)}
    </span>
  );
}

/**
 * Interruptor. É o mesmo em fluxo, limite da delegação e ajuste.
 *
 * `Switch` do shadcn: o trilho, o botão e o deslizar vêm de lá — o que mora
 * aqui é a cor de ligado (verde da casa) e a largura de 42px do desenho.
 */
export function Alternador({ ligado, aoTocar, titulo, rotulo }) {
  return (
    <Switch
      checked={Boolean(ligado)}
      onCheckedChange={aoTocar}
      data-dica={titulo}
      aria-label={rotulo ?? titulo}
      /* A medida (42×24, círculo de 18) e o verde de ligado vivem no tema do
         `Switch` — aqui só sobra o que é do lugar: não encolher no flex. */
      className="flex-none"
    />
  );
}

/**
 * Insere um trecho na POSIÇÃO DO CURSOR de um campo de texto.
 *
 * O chip de variável (Rápidas, modelos de contrato) existe para quem não decora a sintaxe; jogar `{nome}` no
 * fim do texto obrigaria a pessoa a recortar e colar a variável até o lugar
 * onde ela devia estar — o oposto do que o chip promete. O cursor fica DEPOIS
 * do que entrou, senão a letra seguinte nasce antes da variável.
 */
export function inserirNoCursor(area, valor, trecho, aoMudar) {
  const inicio = area?.selectionStart ?? valor.length;
  const fim = area?.selectionEnd ?? valor.length;
  aoMudar(valor.slice(0, inicio) + trecho + valor.slice(fim));
  /* Depois da repintura: o campo é controlado, então o cursor só existe no DOM
     quando o React já escreveu o valor novo. */
  const onde = inicio + trecho.length;
  requestAnimationFrame(() => {
    area?.focus();
    area?.setSelectionRange(onde, onde);
  });
}

/** Título de seção do painel: caixa alta, pequeno, espaçado. */
export function Secao({ children, espaco = "0 0 9px" }) {
  return (
    /* Caixa alta aqui, e não no texto de quem chama: "QUEM CUIDA" vinha escrito
       em maiúsculas e "Reuniões" não, e as seções da mesma coluna saíam com dois
       desenhos. */
    <div className="text-selo font-bold tracking-[.08em] text-muted-foreground uppercase" style={{ padding: espaco }}>
      {children}
    </div>
  );
}

/** Cartão branco padrão das vistas. */
export function Cartao({ children, estilo = "", borda = "var(--color-border)", classe = "" }) {
  /* `Card` do shadcn com a moldura da casa: borda de 1px em vez do anel, e sem
     sombra — na mesa bege o cartão se separa pela borda, e a sombra do preset
     empilhava um degradê cinza sobre o papel.

     `classe` existe pelo mesmo motivo do `Botao`: o cartão da pessoa precisa da
     classe `.cartao` (o levantar no ponteiro, que mora no `index.html`) e de
     borda por estado. Sem isto, a tela chamava o `Card` do registro direto e a
     medida da casa se perdia. Vem depois do padrão, por `cn()`. */
  return (
    <Card
      className={cn("gap-0 rounded-xl border bg-card px-[19px] py-[18px] shadow-none ring-0 [--card-spacing:0px]", classe)}
      style={{ borderColor: borda, ...s(estilo) }}
    >
      {children}
    </Card>
  );
}

/** Linha "rótulo à esquerda, coisa à direita" das listas de ajuste e ficha. */
export function LinhaDeAjuste({ rotulo, ajuda, children, primeira = false }) {
  return (
    <div className={`flex items-center gap-3 py-2.5 ${primeira ? "" : "border-t border-t-foreground/8"}`}>
      <div className="min-w-0 flex-1">
        <div className="text-corpo font-semibold">{rotulo}</div>
        {ajuda && <div className="mt-0.5 text-apoio leading-[1.45] text-muted-foreground">{ajuda}</div>}
      </div>
      {children}
    </div>
  );
}

/** Valor só de leitura — o que o servidor decide e a tela não muda. */
export function Valor({ children }) {
  return (
    <span className="max-w-[52%] flex-none text-right font-mono text-apoio font-semibold break-words text-primary">
      {children ?? "—"}
    </span>
  );
}

/**
 * A barra de progresso que não começa em zero.
 *
 * Efeito de gradiente de meta: quem vê "0 de 5" lê "não comecei e falta tudo" e
 * larga na segunda tela; quem vê o primeiro passo já marcado anda mais rápido
 * para o fim. O truque NÃO é inflar número — é contar o que a pessoa já fez de
 * verdade e ninguém estava creditando: abrir o caso, gravar a ficha, receber a
 * primeira mensagem. `base` é esse crédito, e entra em cima E embaixo (1 de 5
 * vira 2 de 6), porque o passo existe dos dois lados da conta.
 *
 * `base=0` para quando não há passo real a creditar — a barra volta a ser barra.
 */
export function Progresso({ feitos, total, base = 1, rotulo, nota }) {
  const feito = Math.max(0, feitos) + base;
  const cheio = Math.max(Math.max(0, total) + base, 1);
  const pct = Math.min(100, Math.round((feito / cheio) * 100));

  return (
    <div className="min-w-0">
      <div className="mb-[5px] flex items-baseline justify-between gap-2">
        <span className="text-selo font-semibold text-tinta-media">{rotulo}</span>
        <span className="font-mono text-selo font-semibold text-primary">{pct}%</span>
      </div>
      <Progress value={pct} aria-label={`${rotulo}: ${pct}% — ${feito} de ${cheio}`} />
      <div className="mt-[5px] text-apoio leading-[1.45] text-muted-foreground">{nota ?? `${feito} de ${cheio} passos`}</div>
    </div>
  );
}

/** Vazio com voz: diz o que fazer, não só que não há nada. */
/**
 * A lista vazia — e o que fazer a respeito.
 *
 * `acao` e `segunda` existem porque 14 dos 17 vazios da mesa eram BECO: diziam
 * que não havia nada e paravam ali, deixando a pessoa procurar na tela onde se
 * cria a primeira tarefa, a primeira resposta pronta, o primeiro agente. O
 * `EmptyContent` do registro já servia para isso e ninguém usava.
 *
 * Dois vazios NÃO recebem ação, de propósito, e é a regra que impede isto de
 * virar botão em tudo:
 *
 *   - vazio que é BOA NOTÍCIA ("nada pendente agora") — oferecer ação ali é
 *     inventar trabalho;
 *   - vazio que é FALTA DE PERMISSÃO — a saída não é um botão, é o nome de quem
 *     libera, e ele vai no texto.
 *
 * `{ rotulo, aoTocar }` e não um `<Botao>` pronto, porque a medida do botão do
 * vazio é decisão desta peça: a ação de um beco é grande e destacada, e cada
 * tela escolhendo o tamanho era o que fazia os três vazios que já tinham botão
 * parecerem três desenhos diferentes.
 */
export function Vazio({ titulo, children, acao, segunda }) {
  return (
    <Empty className="p-[40px_20px] text-muted-foreground">
      <EmptyHeader className="gap-1.5">
        {titulo && <EmptyTitle className="mb-0 font-serif text-titulo text-foreground">{titulo}</EmptyTitle>}
        <EmptyDescription className="text-corpo leading-[1.55] text-muted-foreground">{children}</EmptyDescription>
      </EmptyHeader>
      {acao || segunda ? (
        <EmptyContent className="mt-1 flex-row flex-wrap justify-center gap-2">
          {acao ? <Botao rotulo={acao.rotulo} aoTocar={acao.aoTocar} titulo={acao.dica} destaque tamanho="toque" /> : null}
          {segunda ? (
            <Botao rotulo={segunda.rotulo} aoTocar={segunda.aoTocar} titulo={segunda.dica} variante="fantasma" tamanho="toque" />
          ) : null}
        </EmptyContent>
      ) : null}
    </Empty>
  );
}

/**
 * WIDGET — a peça da coluna do caso, no idioma de iPadOS.
 *
 * O painel era uma pilha de SEÇÕES: um título em caixa alta, uma régua, e o
 * conteúdo solto embaixo. Isso lê bem num documento e mal numa coluna de 300px
 * que a pessoa varre com o olho vinte vezes por dia — nada tem borda, tudo tem o
 * mesmo peso, e o que importa não se distingue do que só está ali.
 *
 * O widget é o contrário, e é por isso que o telefone inteiro é feito deles:
 *
 *   - **um cartão fechado**, com raio grande, que o olho pega como uma coisa só;
 *   - **um cabeçalho de três caracteres**: ícone, nome curto, e o VALOR à direita
 *     — o número que responde a pergunta antes de qualquer leitura ("2/4", "há
 *     12d", "6/6");
 *   - **um dado glanceável** no meio, grande, em vez de um parágrafo;
 *   - **o cartão inteiro é o alvo** quando ele leva a algum lugar. Não um link de
 *     11px no canto: o dedo acerta o cartão, e o ponteiro também.
 *
 * `tom` é o peso na coluna, e existe para o painel poder ter hierarquia sem
 * inventar cor nova: `papel` é o padrão, `acao` é o que a etapa pede agora
 * (terracota), `escuro` é o da IA, `verde` é o que está resolvido.
 *
 * O que ele NÃO faz: rolar por dentro, ter duas ações primárias, ou crescer com
 * o conteúdo. Widget que rola é seção com borda; widget que não cabe é dois
 * widgets.
 */
export function Widget({ icone, titulo, valor, tom = "papel", aoTocar, dica, marca, largo = false, children }) {
  const pele = {
    papel: "border-border bg-card",
    acao: "border-terra/30 bg-[color-mix(in_oklab,var(--color-terra)_7%,var(--color-card))]",
    verde: "border-verde/28 bg-[color-mix(in_oklab,var(--color-verde)_7%,var(--color-card))]",
    ambar: "border-ambar/35 bg-[color-mix(in_oklab,var(--color-ambar)_9%,var(--color-card))]",
    escuro: "border-escuro bg-escuro text-sobre-escuro",
  }[tom];
  const letraDoTitulo = tom === "escuro" ? "text-sobre-escuro/60" : "text-tinta-fraca";
  const letraDoValor = tom === "escuro" ? "text-sobre-escuro" : tom === "acao" ? "text-terra" : "text-foreground";
  const Caixa = aoTocar ? "button" : "div";

  return (
    <Caixa
      /* `data-secao` é o endereço da coisa na coluna: `?painel=ficha` rola até
         ela e a demonstração aponta a seta para ela. Era o `<Titulo>` da seção
         que a marcava; com a seção virando widget, a marca vem para cá — e
         `roteiro.test.jsx` reprova alvo que não casa, que é como isto apareceu. */
      data-secao={marca}
      {...(aoTocar
        ? {
            type: "button",
            onClick: aoTocar,
            "data-dica": dica,
            /* `data-dica` NÃO é nome acessível — é a regra da casa, e um widget
               clicável sem nome é um botão que o leitor de tela anuncia como
               "botão" e mais nada. O nome é o RÓTULO mais o valor, que é o que
               uma pessoa leria em voz alta: "Documentos, 4/4". A dica continua
               sendo a explicação, para quem vê. */
            "aria-label": [titulo, valor].filter((x) => x != null && x !== "").join(", "),
          }
        : {})}
      /* Raio 18 (superfície) e 12/14 de respiro: o widget é da família da bolha
         e do modal, não do botão. Afunda .985 e não .97 — cartão inteiro
         afundando 3% é a coluna tremendo. */
      className={cn(
        "flex min-w-0 flex-col gap-1.5 rounded-superficie border p-[12px_14px] text-left",
        pele,
        largo && "col-span-2",
        aoTocar &&
          "cursor-pointer transition-[transform,border-color] duration-[var(--t-toque)] ease-entra hover:border-tinta-fantasma active:scale-[.985]",
        aoTocar && tom === "escuro" && "hover:border-sobre-escuro/30",
      )}
    >
      {/*
       * Cabeçalho: só o rótulo. O NÚMERO desceu para o corpo, grande.
       *
       * Ele morava aqui, à direita do nome, e disputava 150px com ele: "4/4"
       * fazia "DOCUMENTOS" virar "DOCUMEN…". E disputava errado — num widget o
       * número é o CONTEÚDO, não a legenda: é ele que responde a pergunta de
       * relance, e é ele que tem de ser grande. É assim que o telefone desenha
       * bateria, passos e clima, pelo mesmo motivo.
       */}
      <span className="flex items-center gap-1.5">
        {icone ? <Icone de={icone} tamanho={14} cor="currentColor" estilo={s(`flex:none;opacity:.6`)} /> : null}
        <span className={`min-w-0 flex-1 truncate text-selo font-bold tracking-[.08em] uppercase ${letraDoTitulo}`}>
          {titulo}
        </span>
      </span>
      {/* 28 na fonte de título (`text-vista`): é o número que se lê do outro lado
          da mesa, e tabular para 9 e 10 ocuparem o mesmo lugar. */}
      {valor != null ? <span className={`font-serif text-vista leading-none tabular-nums ${letraDoValor}`}>{valor}</span> : null}
      {children}
    </Caixa>
  );
}

/**
 * A GRADE dos widgets: duas colunas, e o `largo` atravessa as duas.
 *
 * Duas e não três: a coluna do caso tem 300px, e três widgets de 92px não cabem
 * um número de dois dígitos com o nome do que ele conta. Onde a coluna flutua
 * larga (celular em pé, 412px), continuam duas — mudar a contagem por largura
 * fez o painel se reaprender a cada limiar, e é o defeito que o trilho do
 * processo já custou duas versões.
 */
export function GradeDeWidgets({ children }) {
  return <div className="grid grid-cols-2 items-stretch gap-2">{children}</div>;
}

/**
 * A LINHA-BOTÃO do painel: ETAPA, os sinais do alto, os passos que não são o
 * primeiro. 44 de altura, raio 14, rótulo em selo, valor em peso, seta na ponta.
 *
 * Eram três desenhos da mesma coisa — a linha da etapa, o sinal que rola até a
 * seção e o passo em linha —, com três alturas, e as três abaixo do alvo de
 * dedo. Uma linha que leva a algum lugar é UM controle, e o cartão inteiro é o
 * alvo. Não afunda (`active:transform-none`): linha larga afundando 3% é a
 * coluna tremendo; o que responde ao toque é o fundo.
 *
 * Sem `aoTocar` ela é só leitura, e não finge ser botão.
 */
export function LinhaDoPainel({ rotulo, valor, icone, aoTocar, dica, marca, tom, children, ...resto }) {
  const Caixa = aoTocar ? "button" : "div";
  return (
    /* O resto (`aria-label`, `disabled`, `data-*`) passa: a ETAPA precisa do
       nome "Etapa: …" que o fluxo do Maestro lê, o sinal precisa do valor no
       nome, e o passo trava enquanto executa. */
    <Caixa
      data-secao={marca}
      {...(aoTocar ? { type: "button", onClick: aoTocar, "data-dica": dica } : {})}
      {...resto}
      className={cn(
        "flex min-h-toque w-full min-w-0 items-center gap-2.5 rounded-cartao border border-border bg-card px-3 text-left text-apoio text-tinta-media",
        tom === "acao" && "border-terra/30 bg-[color-mix(in_oklab,var(--color-terra)_7%,var(--color-card))]",
        aoTocar &&
          "cursor-pointer transition-colors duration-[var(--t-toque)] ease-entra hover:border-tinta-fantasma hover:bg-foreground/4 active:transform-none active:bg-foreground/8 disabled:cursor-wait disabled:border-dashed disabled:bg-muted",
      )}
    >
      {icone ? (
        <span className={cn("flex flex-none opacity-70", tom === "acao" && "text-terra opacity-100")}>
          <Icone de={icone} tamanho={16} />
        </span>
      ) : null}
      {rotulo ? (
        <span className="flex-none text-selo font-bold tracking-[.08em] text-tinta-fraca uppercase">{rotulo}</span>
      ) : null}
      <span className="min-w-0 flex-1 truncate font-semibold text-foreground">{valor}</span>
      {children}
      {aoTocar ? <Icone de={ICONES.avancar} tamanho={16} cor="var(--color-tinta-fraca)" estilo={s("flex:none")} /> : null}
    </Caixa>
  );
}

/**
 * A FAIXA acima do composer (e onde mais couber um aviso preso a um lugar):
 * uma geometria, cinco tons.
 *
 * Eram cinco desenhos para a mesma função — a nota interna em âmbar, a
 * citação com o ✕, a IA no comando, o envio agendado e o aviso de fora do
 * horário —, cada um com raio, altura e borda próprios, e a barra de escrever
 * mudava de forma conforme a faixa que estava em cima. Agora o que muda é só o
 * TOM: raio 14, fio de 3px à esquerda (a cor se diz também pela posição), 44 de
 * altura mínima, título em selo caixa alta, texto de apoio.
 *
 * `tom`: `aviso` (terra — atenção), `nota` (âmbar — não sai para o cliente),
 * `ia` (verde — a IA está fazendo), `cita` (verde-fundo — o que se responde),
 * `agenda` (azul — sai depois). `fim` é o que fica na ponta direita (o ✕, um
 * botão mini).
 */
const TONS_DA_FAIXA = {
  aviso: {
    caixa: "border-terra/34 border-l-terra bg-[color-mix(in_oklab,var(--color-terra)_8%,var(--color-card))]",
    letra: "text-terra-escura",
  },
  nota: {
    caixa: "border-ambar/34 border-l-ambar bg-[color-mix(in_oklab,var(--color-ambar)_9%,var(--color-card))]",
    letra: "text-ambar-escuro",
  },
  ia: {
    caixa: "border-verde/30 border-l-verde bg-[color-mix(in_oklab,var(--color-verde)_7%,var(--color-card))]",
    letra: "text-verde",
  },
  cita: { caixa: "border-border border-l-verde-fundo bg-card", letra: "text-verde-fundo" },
  agenda: {
    caixa: "border-azul/28 border-l-azul bg-[color-mix(in_oklab,var(--color-azul)_6%,var(--color-card))]",
    letra: "text-azul",
  },
};

export function Faixa({ tom = "aviso", icone, titulo, children, fim, marca, classe = "" }) {
  const { caixa, letra } = TONS_DA_FAIXA[tom] ?? TONS_DA_FAIXA.aviso;
  return (
    <div
      data-secao={marca}
      className={cn("flex min-h-toque items-start gap-2.5 rounded-cartao border border-l-[3px] p-[10px_12px]", caixa, classe)}
    >
      {icone ? (
        <span className={`mt-px flex-none ${letra}`}>
          <Icone de={icone} tamanho={16} cor="currentColor" />
        </span>
      ) : null}
      <div className="min-w-0 flex-1">
        {titulo ? <div className={`mb-0.5 text-selo font-bold tracking-[.08em] uppercase ${letra}`}>{titulo}</div> : null}
        {children ? <div className="text-apoio leading-[1.45] text-tinta-suave">{children}</div> : null}
      </div>
      {fim ? <div className="flex flex-none items-center gap-1.5 self-center">{fim}</div> : null}
    </div>
  );
}

/**
 * A linha que ACABOU de ser gravada — o realce e o selo de quem fez.
 *
 * Embrulha o que mudou (uma linha do checklist, o funil, um cartão) e, por 1,6
 * segundo depois da escrita, pinta um verde de dentro para fora e escreve
 * "✓ agora, por Camila" ao lado. Não substitui o balão do rodapé: o balão é
 * para quem não estava olhando, este é para quem estava.
 *
 * `chave` é a do objeto — `checklist:12`, `etapa:8` —, a mesma que `estado.jsx`
 * marca em `gravou()`. Quem escreve e quem pisca não precisam se conhecer.
 *
 * A animação respeita `prefers-reduced-motion` pelo `acesso.css`, que zera
 * duração de animação na mesa inteira: para quem pediu menos movimento sobra o
 * selo, que é a informação.
 */
export function Gravou({ chave, children, classe = "" }) {
  const { aceso, autor } = useGravou(chave);
  return (
    <div className={`relative rounded-controle ${aceso ? "animate-[vGravou_1.6s_ease-out]" : ""} ${classe}`}>
      {children}
      {aceso ? (
        <span
          /* `aria-live` para quem não vê o verde: a confirmação é a mesma, e ela
             não pode depender de cor. */
          role="status"
          className="pointer-events-none absolute -top-1.5 right-1 z-[2] animate-[vFade_.2s_var(--e-entra)] rounded-etiqueta bg-verde/14 px-2 py-0.5 text-selo font-bold whitespace-nowrap text-verde"
        >
          ✓ agora{autor ? `, por ${autor}` : ""}
        </span>
      ) : null}
    </div>
  );
}

/**
 * A falha de UM bloco: diz o que não veio e oferece a releitura.
 *
 * Diferente da `Ausencia`, que É a tela quando o endereço não abriu: esta é uma
 * faixa POR CIMA do que já está na tela. Existe porque a lista velha continuar
 * ali é, quase sempre, melhor que apagá-la — e porque continuar ali sem aviso é
 * pior que as duas coisas: quem abriu Rápidas atrás da resposta que acabou de
 * escrever ficava olhando uma lista sem ela, achando que não tinha gravado.
 *
 * Estava dentro de `Ajustes.jsx`; subiu para a casa quando a terceira tela
 * precisou dela, que é a regra.
 */
export function Falha({ children, aoTentar }) {
  return (
    <Alert variant="destructive" className="rounded-cartao border-terra/34 bg-terra/5 p-[14px_16px]">
      <AlertDescription className="text-corpo leading-[1.6] text-terra-escura">
        {children}
        {aoTentar ? (
          <div className="mt-2.5">
            <Botao rotulo="Tentar de novo" destaque aoTocar={aoTentar} />
          </div>
        ) : null}
      </AlertDescription>
    </Alert>
  );
}

/**
 * O esqueleto do que está chegando.
 *
 * "Carregando…" centralizado é uma frase onde vai aparecer uma lista: a coluna
 * fica vazia, a frase some e o conteúdo entra empurrando tudo — a leitura recomeça
 * do zero. O esqueleto ocupa o MESMO espaço das linhas que vêm, então a troca é
 * o bloco ganhando conteúdo, não a tela se remontando.
 *
 * `role="status"` com o rótulo em texto: para quem usa leitor de tela o desenho
 * não diz nada, e a frase que ele substituiu era a única informação.
 *
 * UMA onda para todas as linhas (`.esqueleto`, a mesma `vOnda` da bolha que
 * carrega): cada linha pulsando com o seu atraso era um segundo vocabulário de
 * espera ao lado da onda — e, na mesma tela, duas esperas desenhadas de jeitos
 * diferentes parecem duas coisas acontecendo.
 */
export function Esqueleto({ linhas = 5, altura = 92, raio = 14, rotulo = "Carregando…" }) {
  return (
    <div role="status" className="flex flex-col gap-0.5 py-1">
      <span className="sr-only">{rotulo}</span>
      {Array.from({ length: linhas }, (_, i) => (
        <div key={i} aria-hidden="true" className="esqueleto" style={{ height: altura, borderRadius: raio }} />
      ))}
    </div>
  );
}

/**
 * A tela que explica por que o endereço não abriu — e oferece a saída.
 *
 * Vazio, erro e carregando eram a mesma coisa na mesa: "Carregando…" para
 * sempre. Aqui é o outro lado do `Vazio` — não "não há nada", mas "havia algo e
 * não chegou", com o motivo em português e um botão que faz sentido para AQUELE
 * motivo (voltar num 404, entrar de novo num 401, tentar de novo num tempo
 * esgotado). Quem decide qual é `ausencia()`, em `ausencia.js`.
 *
 * `role="status"` e não `alert`: isto não interrompe a pessoa no meio de outra
 * coisa — isto É o conteúdo da tela agora, e leitor de tela precisa anunciar a
 * troca, que aconteceu sem o foco sair do lugar.
 */
export function Ausencia({
  falha,
  alvo = "atendimento",
  aoTentar,
  aoVoltar,
  rotuloVoltar = "Voltar para a mesa",
  compacta = false,
}) {
  const { titulo, texto, botao } = ausencia(falha, alvo);
  /* Falta de resposta e recusa do servidor pintam de terracota (é falha e pode
     voltar); registro que não existe pinta neutro — não há nada quebrado, o
     endereço é que aponta para fora. */
  const falhou = botao !== "voltar";

  return (
    <Empty
      role="status"
      className={`max-w-[520px] rounded-2xl border ${compacta ? "p-[18px_20px]" : "p-[30px_26px]"} ${
        falhou ? "border-solid border-terra/34 bg-terra/5" : "border-dashed border-foreground/20 bg-transparent"
      } ${compacta ? "" : "m-auto"}`}
    >
      <EmptyHeader className="gap-1.5">
        <EmptyMedia variant="default" className="mb-0 size-auto bg-transparent shadow-none ring-0">
          <Icone
            de={falhou ? ICONES.risco : ICONES.busca}
            tamanho={20}
            cor={falhou ? "var(--color-terra)" : "var(--color-tinta-fantasma)"}
          />
        </EmptyMedia>
        <EmptyTitle className={`font-serif text-foreground ${compacta ? "mt-1.5 text-forte" : "mt-2.5 text-titulo"}`}>
          {titulo}
        </EmptyTitle>
        <EmptyDescription className="text-corpo leading-[1.6] text-tinta-suave">{texto}</EmptyDescription>
      </EmptyHeader>
      <div className="mt-3.5 flex flex-wrap justify-center gap-2">
        {aoVoltar ? <Botao rotulo={rotuloVoltar} destaque={botao === "voltar"} aoTocar={aoVoltar} /> : null}
        {botao === "entrar" ? (
          /* Recarrega em vez de trocar de rota: a sessão caiu, e o que está em
             memória (mesa, caso, canal de eventos) fala com um servidor que já
             não conhece esta aba. */
          <Botao rotulo="Entrar de novo" destaque aoTocar={() => location.assign("/")} />
        ) : null}
        {aoTentar ? <Botao rotulo="Tentar de novo" destaque={botao === "tentar"} aoTocar={aoTentar} /> : null}
      </div>
    </Empty>
  );
}

/**
 * O cabeçalho das vistas de tela cheia — o PAGE HEADER da mesa, um só.
 *
 * Era uma fileira com `children` soltos: cada tela punha contador, vão elástico
 * (`min-w-[4px] flex-1`), `ml-auto` e botões do jeito dela, e o resultado
 * variava de tela para tela — em Contratos os botões ficavam no meio da faixa,
 * nem colados ao título nem na ponta. Agora são LUGARES com nome, e a peça
 * decide onde cada um mora:
 *
 *   - `voltar`  `{ rotulo, para }` — a trilha de volta, acima do título. Para a
 *               tela que não tem item no trilho e só se alcança por
 *               dentro de outra: sem ela, a saída era o botão do navegador.
 *   - `titulo`  o `h1` da tela (o título DA TELA; nenhuma destas convive com a
 *               lista, então não disputa com o `h1` da coluna de conversas).
 *   - `meta`    o que a tela conta de si ("3 modelos", "12 no recorte"), na
 *               linha do título, em tom de apoio.
 *   - `acoes`   os botões da tela, na PONTA DIREITA, alinhados ao título. O
 *               primário vai por último — é onde o olho termina a faixa.
 *   - `ajuda`   o "?" que leva ao capítulo do manual, sempre o último da faixa.
 *   - `descricao` a frase que explica a tela a quem chega pela primeira vez.
 *   - `barra`   busca e filtros: linha inteira abaixo, porque recorte não é
 *               ação e dividir a faixa com os botões era o que empurrava tudo.
 *
 * No telefone a grade muda de forma, não de conteúdo: título e "?" dividem a
 * primeira linha, as ações descem inteiras para a segunda (e rolam de lado em
 * vez de quebrar em três linhas), e a descrição some — ela custava três linhas
 * da única dobra que existe para dizer o que a pessoa já sabe.
 *
 * `children` NÃO existe de propósito: filho solto é como a fileira desalinhada
 * voltaria. `guardas.test.jsx` reprova.
 */
export function CabecalhoDaVista({ titulo, descricao, ajuda, voltar, meta, acoes, barra }) {
  const nav = useNavegacao();
  /* Só as linhas que EXISTEM entram na grade: linha vazia ainda soma o vão, e
     cabeçalho sem `voltar` nem `barra` ganhava 16px de nada. É valor calculado
     em execução (depende de quais lugares vieram), então vai por variável. */
  const linhas = (larg) =>
    [
      voltar && Array(larg).fill("voltar"),
      larg === 2 ? ["titulo", "ajuda"] : ["titulo", "acoes", "ajuda"],
      larg === 2 && acoes && ["acoes", "acoes"],
      /* A descrição não existe abaixo de `md` (ver abaixo), e a linha dela também não. */
      descricao && larg === 3 && Array(larg).fill("desc"),
      barra && Array(larg).fill("barra"),
    ]
      .filter(Boolean)
      .map((l) => `"${l.join(" ")}"`)
      .join(" ");
  return (
    <header
      style={{ "--areas-celular": linhas(2), "--areas-tela": linhas(3) }}
      className={cn(
        "mb-5 grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-3 gap-y-2 [grid-template-areas:var(--areas-celular)]",
        "md:grid-cols-[minmax(0,1fr)_auto_auto] md:gap-x-4 md:[grid-template-areas:var(--areas-tela)]",
      )}
    >
      {voltar ? (
        <button
          type="button"
          onClick={() => nav.ir(voltar.para)}
          className="alvo lin -ml-1 flex w-fit items-center gap-1 rounded-controle px-1 py-0.5 text-apoio font-semibold text-tinta-suave hover:text-foreground [grid-area:voltar]"
        >
          <Icone de={ICONES.voltar} tamanho={15} largura={2} />
          {voltar.rotulo}
        </button>
      ) : null}

      <div className="flex min-w-0 flex-wrap items-baseline gap-x-3 gap-y-0.5 [grid-area:titulo]">
        <h1 className="m-0 min-w-0 font-serif text-[24px] leading-[1.15] font-normal sm:text-vista">{titulo}</h1>
        {meta ? <div className="flex flex-wrap items-center gap-2 text-apoio text-muted-foreground">{meta}</div> : null}
      </div>

      {acoes ? (
        <div className="flex min-w-0 items-center gap-2 [grid-area:acoes] max-md:overflow-x-auto max-md:pb-0.5 max-md:[&>*]:flex-none md:justify-end">
          {acoes}
        </div>
      ) : null}

      {ajuda ? (
        <button
          type="button"
          onClick={() => nav.ir(`/ajuda/${ajuda}`)}
          aria-label={`Abrir a ajuda de ${titulo}`}
          data-dica="Como esta tela funciona"
          className="alvo lin flex size-[32px] flex-none items-center justify-center rounded-full border border-border bg-card text-tinta-suave hover:text-foreground [grid-area:ajuda]"
        >
          <Icone de={ICONES.ajuda} tamanho={16} largura={1.9} />
        </button>
      ) : null}

      {descricao ? (
        <p className="m-0 hidden max-w-[680px] text-corpo leading-[1.6] text-tinta-suave [grid-area:desc] md:block">
          {descricao}
        </p>
      ) : null}

      {barra ? <div className="mt-1 flex min-w-0 flex-wrap items-center gap-2 [grid-area:barra]">{barra}</div> : null}
    </header>
  );
}

/**
 * A moldura das vistas roláveis.
 *
 * `ScrollArea` do shadcn: a barra é a mesma em todo lugar e não é a do sistema
 * — no Windows a barra nativa come 17px da coluna e aparece sempre, o que
 * desalinhava o respiro das vistas em relação ao desenho.
 *
 * A conversa e as listas virtualizadas NÃO passam por aqui de propósito: lá o
 * elemento que rola é lido e escrito por código nosso (`rolagem.js`, o mapa da
 * margem, o virtualizador), e trocar o contêiner por um viewport interno
 * quebraria a conta de "estou no fim".
 */
export function Rolagem({ children, padding }) {
  return (
    <ScrollArea className="h-full">
      {/* O respiro do desenho é de tela grande: 22px de cada lado num aparelho de
          375px são 12% da largura tirados do conteúdo — cartão de prazo com o
          nome do cliente quebrando em três linhas. Continua aceitando `padding`
          para quem precisa de outra medida. */}
      <div className={padding ? undefined : "p-[16px_13px] sm:p-[26px_22px]"} style={padding ? { padding } : undefined}>
        {children}
      </div>
    </ScrollArea>
  );
}

/** O botãozinho de fechar que aparece no canto de toda caixa. */
export function Fechar({
  aoTocar,
  titulo = "Fechar",
  lado = 26,
  tom = "color-mix(in oklab, var(--color-foreground) 8%, transparent)",
  letra = "var(--color-tinta-suave)",
}) {
  return (
    <Button
      variant="ghost"
      size="icon-sm"
      onClick={aoTocar}
      data-dica={titulo}
      aria-label={titulo}
      /* `alvo`: o ✕ é desenhado com `lado` (26px por padrão, que é o desenho) e
         RECEBE o toque num retângulo de 40px — 44 no dedo. Fechar era o controle
         mais errado da mesa: está em toda caixa, no canto, e metade do tamanho
         que um dedo acerta. */
      className="alvo flex-none rounded-controle p-0"
      style={{ width: lado, height: lado, background: tom, color: letra }}
    >
      <Icone de={ICONES.fechar} tamanho={Math.round(lado * 0.55)} largura={2} />
    </Button>
  );
}

/**
 * O fechar que pergunta quando há o que perder.
 *
 * A MESMA função para as quatro saídas de uma caixa: Esc e clique fora (que a
 * `Cortina` liga por dentro), o ✕ e o "Cancelar" do rodapé — este último é
 * montado pelo chamador, e é por isso que ela sai daqui exportada em vez de
 * viver dentro da `Cortina`.
 *
 * `sujo` falso devolve o fechar puro: quem não tem o que perder não pergunta
 * nada. Objeto troca a frase, porque o que se perde não é sempre "o que você
 * mudou" — a senha provisória é "você copiou?".
 */
export const fecharComPergunta = (aoFechar, sujo) => async () => {
  if (sujo) {
    const ok = await confirmar({
      titulo: "Descartar o que você mudou?",
      texto: "O que está na tela não foi gravado e não volta.",
      acao: "Descartar",
      perigo: true,
      ...(typeof sujo === "object" ? sujo : {}),
    });
    if (!ok) return;
  }
  aoFechar?.();
};

/**
 * O fundo escuro dos modais.
 *
 * `Dialog` do shadcn (primitiva do Base UI): Esc fecha, clique fora fecha, o
 * foco fica preso dentro enquanto a caixa está aberta e volta para onde estava
 * ao fechar, e o resto da mesa sai do alcance do leitor de tela.
 *
 * `rotulo` é o nome da caixa: sem ele o leitor de tela anuncia "diálogo" e mais
 * nada. Ele entra como `DialogTitle`, que é o que a primitiva espera —
 * `aria-label` no painel competia com o título quando a caixa tinha um.
 *
 * `antesDoTitulo` é o que acompanha o título sem entrar nele (selo, ícone,
 * botão da foto) — o título é o nome acessível da caixa e não pode virar frase.
 *
 * **Cabeça e pé ficam parados; o meio rola.** Com `titulo` ou `rodape`, a caixa
 * vira três faixas: título em cima, corpo rolando no meio, botões embaixo. É o
 * que faltava — a caixa inteira rolava, então em tela baixa o "Salvar" saía da
 * vista e a pessoa rolava um formulário procurando o botão que confirma. Sem
 * `titulo` e sem `rodape` nada muda: a caixa é uma faixa só, como antes.
 *
 * **`sujo` é o que há para perder.** Uma caixa tem QUATRO saídas — Esc, clique
 * fora, o ✕ e o "Cancelar" do rodapé — e um formulário mexido morria em todas
 * as quatro sem uma palavra. `sujo` liga a pergunta nas três primeiras; a
 * quarta é montada pelo chamador, no `rodape`, e usa o mesmo
 * `fecharComPergunta(aoFechar, sujo)` exportado acima.
 */
/*
 * A caixa vira FOLHA no telefone.
 *
 * Um modal centrado de 520px numa tela de 390 é uma caixa flutuando com dois
 * dedos de sobra em cada lado, longe do polegar e com o teclado do sistema
 * tapando metade quando um campo recebe foco. Aplicativo nenhum faz isso: a
 * caixa nasce colada embaixo, ocupa a largura toda e sobe deslizando — perto da
 * mão, e o movimento diz de onde ela veio e para onde ela volta.
 *
 * `w-full!` com `!`: a largura do chamador vai por estilo INLINE (`min(520px,
 * 100%)`), e só uma regra `!important` ganha dela. É o mesmo motivo do
 * `max-w-none!`.
 */
const FOLHA_NO_CELULAR = [
  "max-[759px]:top-auto! max-[759px]:bottom-0 max-[759px]:left-0 max-[759px]:w-full! max-[759px]:max-w-none! max-[759px]:translate-x-0 max-[759px]:translate-y-0",
  "max-[759px]:rounded-b-none max-[759px]:rounded-t-[22px] max-[759px]:max-h-[92dvh]!",
  /* O zoom do registro é gesto de modal centrado; folha ENTRA DE BAIXO. */
  "max-[759px]:data-open:zoom-in-100 max-[759px]:data-closed:zoom-out-100",
  "max-[759px]:data-open:slide-in-from-bottom-full max-[759px]:data-closed:slide-out-to-bottom-full max-[759px]:duration-[var(--t-media)]",
].join(" ");

/**
 * Arrastar a folha para baixo para fechar.
 *
 * O dedo desce e a caixa DESCE JUNTO — a resposta é contínua, não um botão que
 * some depois. Passou de 96px (ou foi um puxão rápido), fecha; parou antes,
 * volta ao lugar com a curva de entrada. Sem isso a alça é enfeite: um risquinho
 * cinza que promete o gesto que todo aplicativo tem e não faz nada.
 *
 * A captura de ponteiro fica na ALÇA, nunca no corpo: a folha rola por dentro, e
 * um arrasto capturado no conteúdo comeria a rolagem do formulário.
 */
function useArrastoParaFechar(fechar) {
  const [y, setY] = useState(0);
  const origem = useRef(null);

  const pegar = (e) => {
    origem.current = { y: e.clientY, quando: e.timeStamp };
    e.currentTarget.setPointerCapture(e.pointerId);
  };
  const mover = (e) => {
    if (!origem.current) return;
    /* Só para baixo: puxar para cima não estica a folha, ela para no lugar. */
    setY(Math.max(0, e.clientY - origem.current.y));
  };
  const soltar = (e) => {
    const de = origem.current;
    origem.current = null;
    e.currentTarget.releasePointerCapture?.(e.pointerId);
    const dy = de ? e.clientY - de.y : 0;
    /* Distância OU velocidade: o puxão curto e rápido é o gesto que a pessoa faz
       quando já decidiu, e exigir 96px dele faz a folha "resistir". */
    const rapido = de && e.timeStamp - de.quando < 260 && dy > 36;
    setY(0);
    if (dy > 96 || rapido) void fechar();
  };

  return {
    estilo: y ? { transform: `translateY(${y}px)`, transition: "none" } : undefined,
    puxador: { aoPegar: pegar, aoMover: mover, aoSoltar: soltar, puxando: y > 0 },
  };
}

/** O risco da alça, com a área de toque que o dedo precisa em volta. */
function Alca({ aoPegar, aoMover, aoSoltar, puxando }) {
  return (
    <div
      onPointerDown={aoPegar}
      onPointerMove={aoMover}
      onPointerUp={aoSoltar}
      onPointerCancel={aoSoltar}
      /* `touch-none`: sem isso o navegador entende o arrasto como rolagem da
         página e o gesto morre no primeiro pixel. */
      className="flex h-[22px] flex-none touch-none items-center justify-center min-[760px]:hidden"
      aria-hidden="true"
    >
      <span className={`h-[4px] w-[38px] rounded-full transition-colors ${puxando ? "bg-foreground/35" : "bg-foreground/15"}`} />
    </div>
  );
}

/*
 * TRÊS larguras de caixa: 420 (pergunta, escolha curta), 560 (formulário) e 880
 * (tabela, editor, duas colunas). Eram vinte e duas medidas escritas por tela
 * — 440, 520, 600, 620, 640, 720, 760, 820… —, e caixas vizinhas com 40px de
 * diferença parecem erro, não decisão. `largura` em px continua aceita e cai
 * no degrau mais perto (empate sobe: encolher um formulário quebra; sobrar
 * espaço, não). Largura que não é px (`100%`, `min(…)`) passa como veio.
 */
const LARGURAS_DA_CORTINA = { estreita: "420px", media: "560px", larga: "880px" };

const larguraDaCortina = (tamanho, largura) => {
  if (tamanho) return LARGURAS_DA_CORTINA[tamanho] ?? LARGURAS_DA_CORTINA.media;
  const px = /^(\d+)px$/.exec(largura ?? "");
  if (!px) return largura ?? LARGURAS_DA_CORTINA.media;
  const n = Number(px[1]);
  return n < 490 ? LARGURAS_DA_CORTINA.estreita : n < 720 ? LARGURAS_DA_CORTINA.media : LARGURAS_DA_CORTINA.larga;
};

export function Cortina({
  aoFechar,
  sujo = false,
  tamanho,
  largura,
  children,
  z = 46,
  rotulo,
  titulo,
  antesDoTitulo,
  acima,
  rodape,
}) {
  const emFaixas = Boolean(titulo || rodape);
  const nome = titulo ?? rotulo;
  const fechar = fecharComPergunta(aoFechar, sujo);
  const arrasto = useArrastoParaFechar(fechar);

  return (
    <Dialog open onOpenChange={(aberto) => !aberto && void fechar()}>
      <DialogContent
        showCloseButton={false}
        className={`${FOLHA_NO_CELULAR} ${
          emFaixas
            ? /* `max-h-[calc(100dvh-32px)]`: `dvh` porque no celular a barra do
                 navegador entra e sai, e com `vh` o rodapé da caixa ficava
                 embaixo dela. `overflow-hidden` no painel — quem rola é o meio. */
              "flex max-h-[calc(100dvh-32px)] flex-col gap-0 overflow-hidden rounded-superficie border border-border bg-card p-0 text-foreground shadow-[0_30px_70px_-20px_color-mix(in_oklab,_var(--color-escuro)_40%,_transparent)] ring-0 sm:max-w-none"
            : "grid max-h-full gap-0 overflow-y-auto rounded-superficie border border-border bg-card p-[22px] text-foreground shadow-[0_30px_70px_-20px_color-mix(in_oklab,_var(--color-escuro)_40%,_transparent)] ring-0 sm:max-w-none"
        }`}
        /* O véu do `DialogContent` é fixo em `z-50` (ui/dialog.tsx), e o `z` que a
           mesa passa aqui é da era em que a cortina era nossa: 44, 46 — abaixo do
           véu. O modal aparecia ATRÁS do próprio véu desfocado.

           `Math.max` e não `z` fixo: quem precisa empilhar acima de outra
           superfície (o visor de anexo pede 80) continua mandando, e quem passa
           valor baixo não vira no-op silencioso. 51 e não 50 para não depender da
           ordem no DOM entre véu e painel. */
        style={{ width: `min(${larguraDaCortina(tamanho, largura)},100%)`, zIndex: Math.max(z, 51), ...arrasto.estilo }}
      >
        {/* A alça da folha: no telefone a caixa vem de baixo e se arrasta para
            fora, como em aplicativo. Só aparece no celular — no computador a
            caixa é um modal centrado, e não há para onde arrastar. */}
        <Alca {...arrasto.puxador} />
        {emFaixas ? (
          <>
            <div className="flex flex-none items-start gap-3 border-b border-b-border px-[22px] pt-[20px] pb-3.5 max-[759px]:pt-2.5">
              {/* O que acompanha o título (o selo da IA, o ícone de encaminhar, o
                  botão da foto da pessoa) fica AO LADO do `DialogTitle`, nunca
                  dentro: o título é o nome acessível da caixa (`aria-labelledby`),
                  e um botão ali dentro entra na frase que o leitor de tela
                  anuncia — "Cadastrar Trocar a foto Ana Paula". */}
              {antesDoTitulo ? <div className="flex flex-none items-center pt-0.5">{antesDoTitulo}</div> : null}
              <div className="min-w-0 flex-1">
                <DialogTitle className="m-0 font-serif text-titulo leading-tight">{nome}</DialogTitle>
                {/* `acima` é o que explica a caixa: uma frase, e ela fica na
                    faixa parada — descrição que rola com o corpo deixa a pessoa
                    sem o assunto no meio do formulário. */}
                {acima ? <div className="mt-1.5 text-corpo leading-[1.55] text-tinta-suave">{acima}</div> : null}
              </div>
              {aoFechar ? <Fechar aoTocar={fechar} /> : null}
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto px-[22px] py-4">{children}</div>
            {rodape ? (
              /* Os botões, sempre na vista. `flex-wrap` porque "Cancelar ·
                 Começar do zero · Confirmar" não cabe em 420px de celular. E 44
                 de altura: é o botão que FECHA a decisão, o último toque da
                 caixa — errar ali é fazer a outra coisa. */
              <div className="flex flex-none flex-wrap items-center justify-end gap-2 border-t border-t-border bg-papel-fundo px-[22px] py-3.5 max-[759px]:pb-[calc(14px+env(safe-area-inset-bottom))] max-[759px]:*:flex-1 [&>[data-slot=button]]:min-h-toque">
                {rodape}
              </div>
            ) : null}
          </>
        ) : (
          <>
            {rotulo ? <DialogTitle className="sr-only">{rotulo}</DialogTitle> : null}
            {children}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

/**
 * A caixa que não fica no meio da tela — visor de anexo, sino de avisos.
 *
 * Mesma proteção do `Dialog` (Esc, clique fora, foco preso), mas a moldura é
 * quem decide onde ela pousa: o visor ocupa a tela inteira, o sino nasce do
 * canto do trilho. Por isso ela desce até as partes do Base UI em vez de usar
 * o `DialogContent`, que já vem centralizado — aqui a posição é do chamador.
 *
 * `fundo` é o CSS do véu por trás (não só a cor): o visor de anexo desfoca o que
 * está atrás, o sino não põe véu nenhum.
 */
export function CortinaLivre({
  aoFechar,
  children,
  z = 46,
  rotulo,
  fundo = "color-mix(in oklab, var(--color-foreground) 45%, transparent)",
  moldura,
  estilo = "",
  aoTocar,
  /* Onde o foco nasce (um `ref`). O padrão do Dialog foca o primeiro controle —
     no visor era o "Baixar", que abria já com a dica acesa e com o Enter pronto
     para baixar um arquivo que a pessoa só queria ver. */
  focoInicial,
}) {
  const veu = s(`background:${fundo}`);
  return (
    <Dialog open onOpenChange={(aberto) => !aberto && aoFechar?.()}>
      <DialogPortal>
        {/*
         * `fundo` é o CSS do véu, não a cor dele: o visor de anexo manda
         * "color-mix(…var(--color-visor) 94%…);backdrop-filter:blur(6px);…" porque ali o
         * véu escurece E desfoca a mesa. Passar isso para `background:` como se
         * fosse cor dá valor inválido — o véu simplesmente não pinta, e a foto
         * abre por cima da mesa viva. Por isso vai por `s()`, como o resto do
         * desenho.
         *
         * O desfoque do registro (`backdrop-blur-sm`) só sai quando o chamador
         * NÃO pediu desfoque: o sino de avisos abre sem véu (`transparent`) e não
         * pode embaçar a lista atrás dele.
         */}
        <DialogOverlay
          className={`animate-in fade-in-0${veu?.backdropFilter ? "" : " backdrop-blur-none supports-backdrop-filter:backdrop-blur-none"}`}
          style={{ ...veu, zIndex: z }}
        />
        {/*
         * DUAS camadas, e não uma: a `moldura` é onde a caixa pousa (o balão do
         * sino nasce a 82px do trilho; o visor de anexo cobre a tela) e o
         * `estilo` é como ela se desenha. Fundir as duas num elemento só —
         * como esta função chegou a fazer — dá o balão de notificações com o
         * `inset:0` de um visor e o visor com a largura de um balão: quem
         * posiciona passa a disputar com quem pinta.
         *
         * Um acima do véu pelo mesmo motivo do `Cortina`: empatados, quem ganha é
         * a ordem no DOM, e isso é sorte, não decisão.
         */}
        <div style={{ ...s(moldura ?? "position:fixed;inset:0"), zIndex: z + 1 }}>
          <DialogBase.Popup
            aria-label={rotulo}
            onClick={aoTocar}
            initialFocus={focoInicial}
            className="outline-none"
            style={s(estilo)}
          >
            {children}
          </DialogBase.Popup>
        </div>
      </DialogPortal>
    </Dialog>
  );
}

/** O selo "IA" verde, que marca tudo que veio do copiloto. */
/**
 * Os três pontinhos que respiram — o "vem resposta" da casa.
 *
 * Eram dois desenhos com a mesma intenção (o "digitando…" do cliente na trilha
 * e o "trabalhando" do assistente), com espaçamento e atraso diferentes: a
 * mesma espera piscava de dois jeitos na mesma tela.
 */
export function Pontinhos({ cor = "bg-tinta-suave", espaco = "gap-[3px]" }) {
  return (
    <span className={`flex items-center ${espaco}`} aria-hidden="true">
      {[0, 1, 2].map((n) => (
        <span
          key={n}
          className={`size-[5px] rounded-full ${cor} animate-[vPontinho_1.2s_ease-in-out_infinite] ${
            n === 1 ? "[animation-delay:.15s]" : n === 2 ? "[animation-delay:.3s]" : ""
          }`}
        />
      ))}
    </span>
  );
}

/**
 * O "está trabalhando" das conversas com IA — pontinhos e, passados alguns
 * segundos, o relógio contando: a diferença entre "travou?" e "está pensando há
 * 40s" é essa linha. Uma só para os assistentes e para a conversa da peça, que
 * antes contavam a mesma espera com desenhos diferentes (lá pontinhos, aqui uma
 * roda girando dentro de uma bolha).
 */
export function Trabalhando({ rotulo = "trabalhando", relogioApos = 4 }) {
  const [segundos, setSegundos] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setSegundos((s) => s + 1), 1000);
    return () => clearInterval(t);
  }, []);
  return (
    <span className="flex items-center gap-2 text-corpo text-tinta-suave">
      <Pontinhos />
      {rotulo}
      {segundos >= relogioApos ? <span className="font-mono text-selo text-tinta-fantasma">{segundos}s</span> : null}
    </span>
  );
}

/* A letra tem piso de 11 (a escala da casa): "IA" a 9px era o menor texto da
   mesa, justamente o que diz que aquilo veio de máquina. */
export function SeloIA({ tamanho = 18, fonte = 11, fundo = "var(--color-verde)", letra = "var(--color-sobre-terra)" }) {
  return (
    <Badge
      className="flex-none justify-center rounded-etiqueta p-0 font-bold"
      style={{ width: tamanho, height: tamanho, background: fundo, color: letra, fontSize: Math.max(11, fonte) }}
    >
      IA
    </Badge>
  );
}

/**
 * O botão da casa. Seis variantes, três tamanhos, e os estados que faltavam.
 *
 * Fica aqui, e não dentro do editor, porque o CSS do motor de .docx zera o
 * fundo de qualquer <button> sob a classe `docx-editor`: o botão da casa precisa
 * de um lugar próprio, fora daquela moldura.
 *
 * `variante` diz o PAPEL, e a cor segue o papel:
 *
 *   - `destaque` — a ação da casa (Salvar, Criar), em TINTA. Terra era o
 *     primário e era também "vai para o cliente" e "atenção": três sentidos na
 *     mesma cor, e o olho não sabia qual botão manda mensagem.
 *   - `acao` — o que SAI para o cliente (mandar link, enviar contrato), em terra.
 *   - `normal` — o secundário, de contorno (borda de controle 3:1).
 *   - `fantasma` — sem moldura, para a ação que acompanha outra.
 *   - `perigo` — irreversível, no véu destrutivo; `perigoCheio` é o que fecha a
 *     pergunta de apagar.
 *   - `sobreEscuro` — sobre superfície escura (visor, widget da IA).
 *
 * `destaque` (booleano) continua valendo e é o mesmo que `variante="destaque"`.
 *
 * `tamanho`: `mini` 28 (dentro de bolha, linha, cartão), `padrao` 36, `toque`
 * 44 (o que fecha decisão). No dedo o padrão sobe para 44 — é piso, não
 * altura: botão de duas linhas continua crescendo.
 *
 * `carregando` mostra a roda NO LUGAR do rótulo, sem mudar a largura (o rótulo
 * continua ali, transparente — o botão que encolhe ao carregar faz a fileira
 * inteira pular), e só depois de 120ms: resposta rápida não pisca. Enquanto
 * carrega o clique morre e `aria-busy` diz ao leitor de tela o que o desenho diz.
 */
const VARIANTES_DO_BOTAO = {
  destaque: "border-transparent bg-tinta font-semibold text-mesa hover:bg-tinta/86",
  acao: "border-transparent bg-terra font-semibold text-sobre-terra hover:bg-terra-escura",
  /* `editor-peca-botao`: o motor de .docx zera o fundo de botão; esta classe é
     a exceção que ele respeita (`editor-peca.css`). */
  normal: "editor-peca-botao border-borda-controle bg-card font-semibold text-tinta-media hover:bg-muted hover:text-foreground",
  fantasma: "border-transparent bg-transparent font-semibold text-tinta-suave hover:bg-foreground/8 hover:text-foreground",
  perigo: "border-transparent bg-destructive/10 font-semibold text-destructive hover:bg-destructive/18",
  perigoCheio: "border-transparent bg-destructive font-semibold text-sobre-terra hover:bg-destructive/86",
  sobreEscuro:
    "border-sobre-escuro/20 bg-sobre-escuro/10 font-semibold text-sobre-escuro hover:bg-sobre-escuro/20 hover:text-sobre-escuro",
};

const TAMANHOS_DO_BOTAO = {
  mini: { caixa: "min-h-mini gap-1.5 text-apoio", recuo: "px-2.5", icone: "size-mini px-0", desenho: 14 },
  padrao: {
    caixa: "min-h-padrao gap-2 text-corpo max-[759px]:min-h-toque",
    recuo: "px-3.5",
    icone: "size-padrao px-0 max-[759px]:size-toque",
    desenho: 16,
  },
  toque: { caixa: "min-h-toque gap-2 text-corpo", recuo: "px-[18px]", icone: "size-toque px-0", desenho: 16 },
};

export function Botao({
  rotulo,
  /* Ícone no lugar do texto: o "+ Novo" da mesa virou um botão redondo no
     cabeçalho da coluna, e escrever a palavra ali custava metade da fileira.
     Sem `rotulo`, o `titulo` (que já é a dica) vira o nome acessível — botão só
     de ícone sem nome é botão mudo para quem não vê o desenho. */
  icone,
  aoTocar,
  desabilitado,
  destaque,
  variante = destaque ? "destaque" : "normal",
  tamanho = "padrao",
  carregando = false,
  titulo,
  tipo = "button",
  largo = false,
  estilo = "",
  classe = "",
  /* Endereço de download: o botão vira `<a download>`. O navegador já sabe
     salvar o que vem com `content-disposition`, e a planilha não passa pela
     memória da aba — e a tela deixa de montar `Button` cru só para isso. */
  baixar,
}) {
  const medida = TAMANHOS_DO_BOTAO[tamanho] ?? TAMANHOS_DO_BOTAO.padrao;
  const parado = desabilitado || carregando;
  return (
    <Button
      /* `outline` só para o contorno: é ele que acende no `aria-expanded` do
         gatilho de menu. As outras vão sobre `default`, que não traz `dark:` —
         o `dark:bg-transparent` do contorno apagaria o fundo terra no escuro. */
      variant={variante === "normal" ? "outline" : "default"}
      render={baixar ? <a href={baixar} download /> : undefined}
      /* `classe` existe para o chamador ajustar a pele SEM hexadecimal: era só
         `estilo` (string de CSS), e cada botão fora do padrão trazia a cor
         escrita à mão de volta. Vem por último para vencer o padrão daqui —
         `cn()` resolve o conflito de classe em vez de deixar pela ordem da
         folha. O afundar (.97), o anel de foco e o desabilitado legível são do
         `index.css`, iguais para todo botão da mesa. */
      className={cn(
        "relative h-auto items-center whitespace-nowrap rounded-controle",
        medida.caixa,
        rotulo ? medida.recuo : medida.icone,
        VARIANTES_DO_BOTAO[variante] ?? VARIANTES_DO_BOTAO.normal,
        largo ? "flex-1" : "flex-none",
        carregando && "cursor-progress",
        classe,
      )}
      type={tipo}
      /* Desabilitado COM explicação não usa o `disabled` do HTML.
         O navegador não entrega evento de ponteiro a controle desabilitado — e a
         dica da mesa é um ouvinte de `pointerover` no documento. Ou seja: o
         motivo escrito no botão que a pessoa não consegue apertar era
         exatamente o que ela nunca conseguia ler. `aria-disabled` diz a mesma
         coisa ao leitor de tela, mantém o foco alcançável (quem chega pelo
         teclado também merece o motivo) e o clique morre aqui. Sem explicação,
         segue `disabled` de verdade: não há nada a ler. Carregando é o mesmo
         caso sem a pele de desabilitado: o foco fica onde estava. */
      onClick={parado ? (e) => e.preventDefault() : aoTocar}
      disabled={desabilitado && !titulo}
      aria-disabled={desabilitado || undefined}
      aria-busy={carregando || undefined}
      /* A dica vale SEMPRE que alguém escreveu uma. Antes ela era descartada
         quando o botão tinha texto (`rotulo ? undefined : titulo`), e com isso
         26 explicações escritas na mesa nunca chegavam à tela: "Concluir" sem o
         "Dar esta tarefa por feita", "Aprovar" sem o "Deixar o agente executar".
         Nome acessível continua sendo o texto quando ele existe — a dica é
         complemento, não substituto. */
      data-dica={titulo}
      aria-label={rotulo ? undefined : titulo}
      style={s(estilo)}
    >
      <span
        className={cn(
          "inline-flex items-center gap-[inherit] transition-[opacity,filter] duration-[var(--t-curta)]",
          carregando && "opacity-0 blur-[2px] delay-120",
        )}
      >
        {icone ? <Icone de={icone} tamanho={medida.desenho} /> : null}
        {rotulo}
      </span>
      {carregando ? (
        <span
          aria-hidden="true"
          className="absolute inset-0 grid animate-[vFade_var(--t-curta)_var(--e-entra)_120ms_both] place-items-center"
        >
          <Spinner />
        </span>
      ) : null}
    </Button>
  );
}

/**
 * O seletor da casa — o `<select>` do navegador não serve aqui.
 *
 * Dois motivos concretos: a lista nativa é desenhada pelo sistema (fonte, borda
 * e destaque em azul, que não é a paleta do escritório), e na barra do editor
 * ela abriria dentro de uma faixa com `overflow-x` — a lista seria cortada.
 *
 * O `Select` do shadcn resolve os dois: a lista sai num portal ancorado no
 * botão (não é cortada por `overflow`, e vira para cima quando não cabe embaixo),
 * e vem com seta, Home/End, busca por letra e `role=listbox`.
 *
 * Duas peles, mesma peça. A compacta é a das barras (filtro da mesa, tamanho da
 * fonte no editor), onde o seletor divide 34px de altura com botões. `comoCampo`
 * é a de FORMULÁRIO: ao lado de um `Campo`, o seletor compacto parecia meio
 * input e meio botão — outra altura, outro fundo, outro peso de letra na mesma
 * linha.
 */
export function Seletor({
  rotulo,
  valor,
  opcoes,
  aoEscolher,
  largura = "128px",
  /**
   * Altura exata, para régua de ferramentas.
   *
   * O padrão (`py-1.5` sobre 11,5px) dá 36px, e ao lado de um botão de ícone de
   * 30 isso desalinhava a barra do editor inteira: o grupo passava a medir 36, e
   * os quatro botões de alinhamento ficavam 3px ACIMA dos do grupo vizinho. Numa
   * régua, o que tem de ser igual é a altura — não o respiro interno.
   */
  altura,
  desabilitado,
  alinhar = "esquerda",
  comoCampo = false,
  /** O que o gatilho diz quando nada foi escolhido. "—" sozinho num campo de
   *  formulário (a categoria da resposta nova, a cor do setor) não dizia nem
   *  que ali se escolhe algo; na régua compacta o traço continua. */
  vazio = comoCampo ? "Escolha…" : "—",
  /** Sem caixa até o ponteiro ou o foco chegar — o campo da ficha, que se lê
   *  como cadastro e não como formulário. */
  limpo = false,
}) {
  const escolhida = opcoes.find((o) => o.id === valor);
  const travado = Boolean(desabilitado) || !opcoes.length;

  return (
    <Select value={valor ?? ""} onValueChange={(v) => aoEscolher(v)} disabled={travado}>
      <SelectTrigger
        data-dica={rotulo}
        aria-label={rotulo}
        /* Cancelar o mousedown é o que preserva a seleção do texto quando o
           seletor está numa barra de formatação: sem isso o clique recolhe a
           marcação do editor e o "13pt" passaria a valer só para o que for
           digitado depois. Não impede a abertura, que acontece no `pointerdown`. */
        onMouseDown={(e) => e.preventDefault()}
        /* No telefone o seletor de FORMULÁRIO cresce para a medida de campo de
           aplicativo (44px, letra de 15): ao lado de um `Campo` — que o sistema
           já obriga a ter 16px de letra para não dar zoom — um gatilho de 13,5px
           parecia outro controle, meio botão e meio rótulo. O compacto (barra de
           filtro, régua do editor) fica como está: ali a altura é a da régua. */
        /* A pele é a do `Campo` (`pele.ts`): 36, 44 no dedo, raio 10, borda de
           controle, foco em borda terra com halo. O anel dos botões sai
           (`outline-none!`, que precisa vencer a regra fora de camada): o
           gatilho é um botão, mas se lê como campo, e campo com anel e halo ao
           mesmo tempo eram dois focos. Aberto, fica com a borda de foco — é
           onde a escolha vai cair. */
        className={cn(
          CAMPO,
          "justify-between gap-2 text-left text-tinta-media focus-visible:outline-none! data-popup-open:border-terra",
          /* O registro fixa a altura pelo `data-size`, que pesa mais que `h-`. */
          "py-0 data-[size=default]:h-padrao max-[759px]:data-[size=default]:h-toque",
          comoCampo
            ? "flex-[1_1_auto] font-normal max-[759px]:text-forte"
            : /* Com `altura` dada, a medida é de quem desenha a faixa (a régua do
                 editor) e vai inline, vencendo os 36/44 daqui. */
              "flex-none px-2.5 text-apoio font-semibold",
          limpo && "border-transparent bg-transparent px-2 text-foreground hover:border-transparent hover:bg-foreground/4",
        )}
        style={{ width: largura, ...(altura ? { height: altura, paddingTop: 0, paddingBottom: 0 } : {}) }}
      >
        {/* O `truncate` vai no SPAN, não no `SelectValue`: aquele é `display:flex`
            (é o registro do shadcn), e reticência não corta filho de flex — a
            régua do editor mostrava "Times New Ro" sem os três pontos, cortado no
            meio da palavra como se a fonte se chamasse assim. */}
        <SelectValue className="min-w-0 flex-1">
          <span className="block min-w-0 truncate">{escolhida?.rotulo ?? vazio}</span>
        </SelectValue>
      </SelectTrigger>
      <SelectContent
        align={alinhar === "direita" ? "end" : "start"}
        alignItemWithTrigger={false}
        sideOffset={4}
        className="max-h-[280px] min-w-[168px] rounded-cartao border p-1.5 max-[759px]:max-h-[56dvh] max-[759px]:min-w-[220px]"
      >
        {opcoes.map((o) => (
          <SelectItem
            key={o.id}
            value={o.id}
            onMouseDown={(e) => e.preventDefault()}
            className="min-h-padrao rounded-controle px-2.5 py-1.5 text-corpo font-medium max-[759px]:min-h-toque max-[759px]:px-3 data-selected:bg-verde-fundo/12 data-selected:font-bold data-selected:text-verde-fundo"
          >
            <span className="min-w-0 flex-1 overflow-hidden text-ellipsis whitespace-nowrap">{o.rotulo}</span>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

/**
 * O menu de ações que não couberam na barra, e a lista de respostas prontas.
 *
 * `DropdownMenu` do shadcn: o gatilho ganha `aria-expanded`, a lista ganha
 * `role=menu`, e seta, Esc e clique fora funcionam sem estado global — o que
 * apagou os `mostrarMais`/`mostrarProntas` que viviam no estado da mesa só para
 * dizer se uma caixinha estava aberta.
 *
 * `estilo` é função de `{ aberto }` porque o gatilho muda de cor quando o menu
 * abre; por isso o `open` é controlado aqui, e não deixado por conta da
 * primitiva. O gatilho é o próprio botão do menu — envolver um `<span>` num
 * botão de mentira custaria o foco pelo teclado.
 */
/* Referência estável: default inline recria a função a cada render. */
const SEM_VESTIDO = () => "";

export function MenuDeAcoes({
  /* Sem vestido o menu ainda tem de abrir: `estilo` opcional é o que impede
     que um chamador novo derrube a VISTA INTEIRA com "e is not a function" —
     foi o que matou /agentes-ia. */
  estilo = SEM_VESTIDO,
  classe,
  children,
  itens,
  rotulo,
  titulo,
  legenda,
  ancora = "bottom end",
  largura = "196px",
  /* O teto do menu é o que cabe na tela, e um menu de área inteira (sete
     lugares com legenda) não cabe em 280px: o último item ficava metade fora,
     atrás de uma rolagem que ninguém vê num menu curto. */
  altura = "280px",
}) {
  const [aberto, setAberto] = useState(false);
  const [lado, alinhamento = "center"] = ancora.split(" ");

  return (
    <DropdownMenu open={aberto} onOpenChange={setAberto}>
      <DropdownMenuTrigger
        /* `alvo`: o gatilho é desenhado pelo chamador (26px na linha de uma
           tarefa, 23px na de uma conversa) e no dedo isso é metade do alvo
           mínimo — a classe põe 44px invisíveis em volta sem mexer no desenho.
           Aqui e não em cada chamador: são treze menus na mesa. */
        className={`alvo ${classe ?? ""}`}
        data-dica={titulo}
        aria-label={rotulo}
        style={s(estilo({ aberto }))}
      >
        {children}
      </DropdownMenuTrigger>
      <DropdownMenuContent
        side={lado}
        align={alinhamento}
        sideOffset={6}
        /* Raio 14 e a abertura NASCENDO do gatilho: o registro já põe
           `origin-(--transform-origin)` (o ponto do gatilho que a primitiva
           calcula), mas desliga a animação (`animate-none!`) — então a
           escala de .96 vai por transição, com os estados de entrada e saída
           do Base UI. Sai mais rápido do que entra. */
        className="max-w-[min(340px,92vw)] rounded-cartao p-1.5 transition-[opacity,scale] duration-[var(--t-curta)] ease-entra data-ending-style:scale-96 data-ending-style:opacity-0 data-ending-style:duration-[var(--t-toque)] data-starting-style:scale-96 data-starting-style:opacity-0"
        style={{ minWidth: largura, maxHeight: altura }}
      >
        {/*
          A legenda é rótulo DE GRUPO, e o Base UI cobra isso: `GroupLabel` fora
          de um `<Menu.Group>` estoura com "MenuGroupContext is missing" e o menu
          abre vazio. Envolver os itens junto é o que faz o leitor de tela
          anunciar "Enquete, agendamento e respostas prontas: 5 itens".
        */}
        {legenda ? (
          <DropdownMenuGroup>
            <DropdownMenuLabel className="px-[9px] pt-1.5 pb-2 text-selo font-bold tracking-[.08em] text-muted-foreground">
              {legenda}
            </DropdownMenuLabel>
          </DropdownMenuGroup>
        ) : null}
        {itens.map((item) => (
          <DropdownMenuItem
            key={item.chave ?? item.rotulo}
            /* Sem o embrulho, o EVENTO do clique vira o primeiro argumento da
               ação — e ação que aceita alvo opcional (`apagarCaso(alvo)`) saía
               pedindo `/api/records/[object Object]`. Item de menu não usa o
               evento. */
            onClick={() => item.aoTocar()}
            /* Item de menu no telefone é LINHA DE LISTA: 46px e letra de 14,5.
               O menu é a mesma peça no computador (onde 12,5px sobre 30px é a
               medida do desenho) e no celular, onde ele é a única forma de
               chegar às ações que saíram da barra — errar o alvo aqui é abrir a
               ação vizinha. */
            className={cn(
              "min-h-padrao gap-2.5 rounded-controle px-2.5 py-2 text-corpo font-semibold text-tinta-media max-[759px]:min-h-toque max-[759px]:px-3",
              item.detalhe ? "items-start" : "items-center",
              /* Perigo é a cor DESTRUTIVA, não a terra: terra é "vai ao
                 cliente" e "atenção", e apagar não é nenhum dos dois. */
              item.perigo && !item.cor && "text-destructive focus:text-destructive data-highlighted:text-destructive",
            )}
            style={item.cor ? { color: item.cor } : undefined}
          >
            {/* `rosto` no lugar do ícone: item que é UMA PESSOA mostra a cara
                dela, como a lista da mesa e a caixa de transferir já fazem. O
                menu de "passa a cuidar" imprimia cinco vezes o mesmo bonequinho
                cinza ao lado de cinco nomes, com as fotos da equipe visíveis na
                coluna atrás — escolher colega por leitura de nome em caixa alta
                é mais lento que reconhecer o rosto, que é o que a mesa toda
                aposta. */}
            {item.rosto ?? null}
            {/* Com detalhe embaixo, o item fica alto e o ícone tem de encostar
                na PRIMEIRA linha do texto, não no topo da caixa. */}
            {!item.rosto && item.icone ? (
              <Icone
                de={item.icone}
                tamanho={16}
                largura={1.8}
                /* A linha do texto tem 18px (12,5 × 1,45) e o ícone tem 16:
                   1px em cima é o que faz ele nascer no centro dela. */
                estilo={s(`flex:none;${item.detalhe ? "margin-top:1px" : ""}`)}
              />
            ) : null}
            {item.marca ? (
              /* `line-height` em px, não em múltiplo: a caixa de linha do mono a
                 10px é menor que a do texto a 12,5px, e com `items-start` as duas
                 bases não coincidiam — o atalho flutuava acima da frase. */
              <span className="flex-none font-mono text-selo leading-[18px] font-bold text-primary">{item.marca}</span>
            ) : null}
            {/* `flex-1`: o rótulo ocupa a linha inteira, para que um rótulo
                composto (nome à esquerda, número na ponta) alcance a borda. */}
            <span className="min-w-0 flex-1">
              {item.rotulo ? <span className="block">{item.rotulo}</span> : null}
              {item.detalhe ? (
                <span className="block text-corpo leading-[1.45] font-medium text-tinta-media">{item.detalhe}</span>
              ) : null}
            </span>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/**
 * Escolher uma entre várias, em cartões — modo da delegação, motor do copiloto.
 *
 * `RadioGroup` do shadcn: a seta anda entre as opções, o grupo tem um nome só e
 * cada cartão anuncia se está marcado. Os `<button>` que faziam isso antes
 * eram, para um leitor de tela, quatro botões soltos sem relação nenhuma.
 *
 * `extra` é o que aparece dentro do cartão marcado (a régua de etapas do modo
 * "até uma etapa" é o caso que existe).
 */
export function Escolha({ rotulo, valor, aoEscolher, opcoes, direcao = "coluna", extra }) {
  return (
    <RadioGroup
      value={valor ?? null}
      onValueChange={(v) => aoEscolher(v)}
      aria-label={rotulo}
      className={direcao === "linha" ? "flex items-stretch gap-1.5" : "flex flex-col gap-2"}
    >
      {opcoes.map((o) => {
        const marcado = o.id === valor;
        return (
          <Label
            key={o.id}
            className={`flex cursor-pointer items-start gap-3 rounded-xl border-[1.5px] p-[12px_13px] ${
              direcao === "linha" ? "flex-1" : ""
            } ${marcado ? "border-verde bg-verde-fundo/8" : "border-border bg-secondary"}`}
          >
            {direcao === "coluna" ? (
              <RadioGroupItem
                value={o.id}
                className="mt-0.5 size-[18px] flex-none border-[1.5px] data-checked:border-verde data-checked:bg-verde"
              />
            ) : (
              /* Em linha, o cartão inteiro é o alvo e o círculo sairia sobrando:
                 o controle continua existindo, escondido, para o teclado e o
                 leitor de tela. */
              <RadioGroupItem value={o.id} className="sr-only" />
            )}
            <span className="min-w-0 flex-1">
              <span className={`block font-bold ${direcao === "linha" ? "text-corpo" : "text-corpo"}`}>{o.titulo}</span>
              {o.detalhe ? (
                <span
                  className={`mt-[3px] block leading-[1.45] text-tinta-suave ${direcao === "linha" ? "text-selo" : "text-apoio"}`}
                >
                  {o.detalhe}
                </span>
              ) : null}
              {/* O que mora dentro do cartão marcado tem teclas próprias (a
                  régua de etapas também é um radiogroup): sem cortar aqui, a
                  seta andaria nos dois grupos ao mesmo tempo. */}
              {marcado && extra ? (
                <span onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
                  {extra(o)}
                </span>
              ) : null}
            </span>
          </Label>
        );
      })}
    </RadioGroup>
  );
}

/**
 * A mesma escolha, em pastilhas — filtro da lista, etapa de parada da delegação.
 *
 * `ToggleGroup` do shadcn no modo de uma só marcada: quem chega pelo teclado
 * anda de pastilha em pastilha com a seta, e o grupo se anuncia como um só.
 * Desmarcar não existe aqui (um filtro sempre vale algum) — por isso a troca só
 * acontece com valor novo.
 */
/* A partir daqui a pastilha pode cortar (180px de largura, na letra de apoio). */
const LETRAS_DA_FICHA = 24;

export function Fichas({ rotulo, valor, aoEscolher, opcoes, estilo = "", compacto = false }) {
  return (
    <ToggleGroup
      value={valor == null ? [] : [valor]}
      onValueChange={(marcados) => {
        const proximo = marcados.find((m) => m !== valor) ?? marcados[0];
        if (proximo != null && proximo !== valor) aoEscolher(proximo);
      }}
      aria-label={rotulo}
      /* Compacto é UMA fileira, sempre: `nowrap` e as pastilhas dividindo a
         largura entre si. Quebrar em duas fileiras numa coluna de 300px comia a
         altura de três conversas — e a fileira que muda de altura conforme o
         filtro escolhido faz a lista inteira pular. */
      /* No celular a fileira ROLA em vez de quebrar: sete filtros por extenso
         viravam três fileiras — 100px da única dobra do aparelho gastos com a
         barra de filtro, antes de aparecer a primeira tarefa. */
      className={
        compacto
          ? "flex flex-nowrap items-center justify-between gap-1"
          : "flex gap-1.5 max-sm:flex-nowrap max-sm:overflow-x-auto max-sm:pb-1 sm:flex-wrap"
      }
      style={s(estilo)}
    >
      {opcoes.map((o) => (
        <ToggleGroupItem
          key={o.id}
          value={o.id}
          /* Pastilha vazia recua, mas continua clicável: o recorte que não tem
             ninguém hoje some do olho sem sumir da mão — apagá-la esconderia
             que "Fila" existe, e desabilitá-la tiraria a única maneira de ver
             que ela está mesmo em zero. */
          data-vazio={o.contador === 0 ? "" : undefined}
          /* Compacto: o ícone é a pastilha e a PALAVRA só aparece na escolhida.
             Sete filtros escritos por extenso ocupavam duas fileiras numa coluna
             de 340px — mais alto que três conversas. O nome não se perde: vai na
             dica e no nome acessível, e a que está valendo diz o seu em texto. */
          data-dica={
            compacto
              ? `${o.rotulo}${o.contador != null ? ` · ${o.contador}` : ""}`
              : typeof o.rotulo === "string" && o.rotulo.length > LETRAS_DA_FICHA
                ? o.rotulo
                : undefined
          }
          aria-label={compacto ? `${o.rotulo}${o.contador != null ? `, ${o.contador}` : ""}` : undefined}
          /* A PÍLULA: 32 de altura, recuo de 14, 13/600, o número numa cápsula de
             20 colada à direita. Ligada é tinta cheia com a letra da mesa — o
             par que inverte junto no escuro (`foreground` com `creme` não
             inverte, e a ligada ficava clara sobre clara). */
          className={`group h-8 rounded-full border border-borda-controle bg-card font-semibold whitespace-nowrap text-tinta-media hover:bg-foreground/4 hover:text-foreground data-pressed:border-tinta data-pressed:bg-tinta data-pressed:text-mesa data-[vazio]:border-foreground/12 data-[vazio]:text-tinta-fraca data-[vazio]:data-pressed:text-mesa ${
            compacto ? "relative size-8 flex-none justify-center p-0" : "gap-2 px-3.5 text-[13px]"
          }`}
        >
          {o.icone ? <Icone de={o.icone} tamanho={compacto ? 16 : 14} /> : null}
          {/* Nome de pessoa vira pastilha, e o de quem tem quatro sobrenomes
              esticava a fileira inteira: corta com reticência, e a dica diz o resto. */}
          {compacto && o.icone ? null : <span className="max-w-[180px] truncate">{o.rotulo}</span>}
          {compacto ? (
            /* O número sai de dentro da pastilha e vira selo no canto: é o que
               tira a largura do texto sem tirar a informação. Zero não desenha
               selo nenhum — a pastilha apagada já diz que não há ninguém ali. */
            <Contador n={o.contador} tom="tinta" sobre classe="absolute -top-1.5 -right-1.5" />
          ) : o.contador != null ? (
            <span className="-mr-1.5 inline-grid h-5 min-w-[22px] place-items-center rounded-full bg-foreground/8 px-1.5 text-selo leading-none font-bold tabular-nums text-tinta-suave group-data-pressed:bg-mesa/20 group-data-pressed:text-mesa">
              {contagem(o.contador)}
            </span>
          ) : null}
        </ToggleGroupItem>
      ))}
    </ToggleGroup>
  );
}

/**
 * Marcar ou desmarcar uma coisa só — o fato que entra na peça.
 *
 * `Checkbox` do shadcn, e não um `<button>` com um ✓ desenhado dentro: o papel
 * é `checkbox`, o estado vai em `aria-checked` e a barra de espaço marca.
 */
export function Marcador({ marcado, aoAlternar, rotulo, children, estilo = "" }) {
  return (
    <Label className="flex cursor-pointer items-start gap-[11px]" style={s(estilo)}>
      <Checkbox
        checked={Boolean(marcado)}
        onCheckedChange={aoAlternar}
        aria-label={rotulo}
        /* Borda de controle (3:1): a caixinha a 25% da tinta sumia no papel —
           não dava para ver que ali se marca. */
        className="mt-px size-[18px] flex-none rounded-etiqueta border-[1.5px] border-borda-controle data-checked:border-verde data-checked:bg-verde data-checked:text-sobre-terra"
      />
      {children}
    </Label>
  );
}

/**
 * Campo de texto com rótulo de verdade.
 *
 * `Field` amarra rótulo e campo; por padrão o rótulo fica fora da vista porque
 * o desenho da mesa pede caixa só com placeholder — e placeholder não serve de
 * nome acessível.
 *
 * `mostrarRotulo` traz o rótulo para a tela. É o que um FORMULÁRIO pede: numa
 * ficha de cadastro com seis caixas, o placeholder some assim que se digita a
 * primeira letra e ninguém mais sabe qual campo é qual.
 */
export function Campo({
  rotulo,
  valor,
  aoMudar,
  tipo = "text",
  dica,
  autoPreencher,
  foco,
  estilo = "",
  moldura = "",
  mostrarRotulo = false,
  ajuda,
  /* A mensagem de erro, em português, embaixo do campo. Vem amarrada por
     `aria-describedby` e liga `aria-invalid`: a borda vermelha sozinha não diz
     nada a quem não a vê, e nem diz a quem vê o que está errado. */
  erro,
  desabilitado = false,
}) {
  /* O rótulo AMARRADO ao campo: o `Label` do registro é um `<label>` sem
     `htmlFor`, e o `Input` não é o `Field.Control` do Base UI — então os dois
     só estavam lado a lado. Tocar no rótulo não focava nada, e o leitor de tela
     lia um campo de senha sem nome (a dica some quando se digita). */
  const id = useId();
  const idDoErro = `${id}-erro`;
  const invalido = erro ? { "aria-invalid": true, "aria-describedby": idDoErro } : {};
  return (
    <Field className="gap-0" style={s(moldura)}>
      {/* `w-px!`: o `Field` do registro põe `w-auto` no `.sr-only`, e o rótulo
          escondido (absoluto, sem quebra) media a frase inteira — "E-mail de
          entrada de <nome comprido>" abria rolagem de lado dentro da caixa. */}
      <FieldLabel
        htmlFor={id}
        className={
          mostrarRotulo ? "mb-[5px] block text-apoio font-semibold text-tinta-media max-[759px]:text-corpo" : "sr-only w-px!"
        }
      >
        {rotulo}
      </FieldLabel>
      {mostrarRotulo && ajuda ? (
        <FieldDescription className="-mt-0.5 mb-1.5 text-apoio leading-[1.4] text-muted-foreground">{ajuda}</FieldDescription>
      ) : null}
      {tipo === "date" || tipo === "datetime-local" ? (
        /* Data é do calendário da casa, nunca do navegador (ver `CampoDeData`). */
        <CampoDeData
          id={id}
          rotulo={rotulo}
          valor={valor}
          aoMudar={aoMudar}
          comHora={tipo === "datetime-local"}
          dica={dica ?? "dd/mm/aaaa"}
          /* O gatilho da data é um botão: sai o anel, fica o foco de campo. */
          className={`${CAMPO} focus-visible:outline-none!`}
        />
      ) : (
        <Input
          id={id}
          type={tipo}
          value={valor}
          onChange={(e) => aoMudar(e.target.value)}
          placeholder={dica ?? rotulo}
          autoComplete={autoPreencher}
          autoFocus={foco}
          disabled={desabilitado || undefined}
          {...invalido}
          /* 44 no telefone: a letra do campo já é obrigada a 16px lá (senão o
           iOS dá zoom e não volta — ver `index.css`), e 36 em volta de 16px é
           uma caixa apertada ao lado de um rótulo de formulário. */
          className={CAMPO}
          style={s(estilo)}
        />
      )}
      {erro ? (
        <p id={idDoErro} className="mt-1.5 text-apoio leading-[1.4] font-medium text-destructive">
          {erro}
        </p>
      ) : null}
    </Field>
  );
}

/**
 * A caixa de busca: ícone, campo e o ✕ que limpa.
 *
 * `InputGroup` do shadcn — é ele que resolve o que era feito à mão aqui: o
 * ícone e o botão de limpar ficam DENTRO da moldura, mas o alvo de clique
 * inteiro é o campo. O respiro vertical continua sendo do campo, e não da
 * caixa: mesma altura na tela, e o dedo que encosta em qualquer ponto da faixa
 * acerta o input em vez da moldura (medido no celular: era 18px de alvo, virou
 * 36).
 */
export function CampoDeBusca({ rotulo, valor, aoMudar, dica, atalho, campo, ocupado = false }) {
  return (
    /* O foco se diz no ANEL da caixa inteira, e não só no cursor piscando: a
       lupa e o botão de limpar moram dentro da mesma borda, então é a borda que
       precisa acender. `focus-within` porque o elemento focado é o input lá
       dentro. */
    <InputGroup
      className={cn(
        "h-padrao rounded-controle border border-borda-controle bg-card px-[11px] hover:border-tinta-fraca max-[759px]:h-toque",
        /* O registro acende a borda pelo `:has()` do campo, que pesa mais que o
           `focus-within`: as duas regras vão na cor da casa. */
        "transition-[border-color,box-shadow] has-[[data-slot=input-group-control]:focus-visible]:border-terra has-[[data-slot=input-group-control]:focus-visible]:ring-0",
        FOCO_DENTRO,
      )}
    >
      <InputGroupAddon align="inline-start" className="pl-0">
        {/* A espera aparece NO LUGAR da lupa: um selo novo em qualquer outro
            canto empurraria o campo, e o que se quer dizer é "esta busca está
            acontecendo", que é justamente o que a lupa representa. */}
        {ocupado ? (
          <Spinner className="size-4 flex-none text-tinta-fraca" />
        ) : (
          <Icone de={ICONES.busca} tamanho={16} cor="var(--color-tinta-fraca)" estilo={s("flex:none")} />
        )}
      </InputGroupAddon>
      <InputGroupInput
        ref={campo}
        aria-label={rotulo}
        value={valor}
        onChange={(e) => aoMudar(e.target.value)}
        placeholder={dica ?? rotulo}
        className="h-full min-w-0 flex-1 border-none bg-transparent py-0 text-corpo text-foreground placeholder:text-tinta-fraca"
      />
      {valor ? (
        <InputGroupAddon align="inline-end" className="pr-0">
          <InputGroupButton
            variant="ghost"
            size="icon-xs"
            onClick={() => aoMudar("")}
            data-dica="Limpar a busca"
            aria-label="Limpar a busca"
          >
            <Icone de={ICONES.fechar} tamanho={14} largura={2} />
          </InputGroupButton>
        </InputGroupAddon>
      ) : atalho ? (
        /* A tecla que traz o foco para cá, dita onde ela serve. Some assim que
           há texto: aí o lugar é do botão de limpar, e dois selos na mesma
           ponta viram enfeite. */
        <InputGroupAddon align="inline-end" className="pr-0">
          <Kbd className="h-5 min-w-5 rounded-etiqueta bg-foreground/8 px-[5px] font-mono text-selo text-tinta-fraca">
            {atalho}
          </Kbd>
        </InputGroupAddon>
      ) : null}
    </InputGroup>
  );
}

/**
 * A peça nascendo: o texto que o modelo está escrevendo, na hora.
 *
 * Montar uma inicial leva perto de um minuto. Tela parada nesse tempo é
 * indistinguível de tela travada — e o que tranquiliza não é uma barra girando,
 * é ver a própria peça aparecendo linha a linha. Rola sozinho para o fim
 * enquanto chega texto, e para de rolar se a pessoa subir para ler.
 */
export function PecaNascendo({ texto, titulo = "Montando a peça a partir do acervo", aoCancelar }) {
  const caixa = useRef(null);
  useEffect(() => {
    const el = caixa.current;
    if (!el) return;
    /* Só arrasta o fim para a vista se a pessoa já estava no fim: subir para ler
       um parágrafo não deve ser desfeito pelo pedaço seguinte. */
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 80) el.scrollTop = el.scrollHeight;
  }, [texto]);

  return (
    <div className="absolute inset-0 z-20 flex flex-col items-center bg-mesa/94 p-[20px_16px]">
      <div className="mb-2.5 flex items-center gap-2 text-corpo font-bold text-verde-fundo">
        <span className="h-3.5 w-0.5 animate-[vCaret_.9s_infinite] bg-verde" />
        {titulo}
        <span className="font-mono font-medium text-muted-foreground">{(texto ?? "").length} caracteres</span>
        {/* guarda-ok: pergunta em quem passa `aoCancelar` — hoje `cancelarMinuta` (estado.jsx). */}
        {aoCancelar ? <Botao rotulo="Cancelar" aoTocar={aoCancelar} titulo="Corta o pedido; nada é gravado" /> : null}
      </div>
      <div
        ref={caixa}
        className="min-h-0 w-full max-w-[720px] flex-1 overflow-y-auto rounded-cartao border border-border bg-card p-[28px_32px] shadow-[0_8px_30px_color-mix(in_oklab,_var(--color-foreground)_8%,_transparent)]"
      >
        {texto ? (
          texto.split(/\n{2,}/).map((paragrafo, i) =>
            paragrafo.startsWith("#") ? (
              <div key={i} className="mb-3 text-corpo font-bold tracking-[.1em] text-foreground">
                {paragrafo.replace(/^#+\s*/, "")}
              </div>
            ) : (
              <div key={i} className="mb-[18px] indent-[2.2em] text-justify text-corpo leading-[1.85] text-tinta-media">
                {paragrafo}
              </div>
            ),
          )
        ) : (
          <div className="text-corpo leading-[1.6] text-muted-foreground">
            Lendo o acervo do escritório e os fatos marcados… o texto aparece aqui conforme o modelo escreve.
          </div>
        )}
      </div>
    </div>
  );
}
